import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useRectificationStore } from '../../../stores';
import { ZoneColors } from '../../../constants/carveReliefConfig';
import { CreateCarveReliefPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import {
  generateCurvePoints,
  projectCurvePointsOntoMesh,
  projectPointOntoMesh,
  getSurfaceNormalAtPoint,
  convertToWorldPositions
} from '../trimline/trimlineHelpers';
import { EASING_FUNCTION, DEFAULT_EASING_FUNCTION, applyEasing } from '../../../utils/EasingFunctions';
import { geometryWorkerPool } from '../../../utils/WorkerPool.js';
import { computeVertexDeformationAsync } from './CarveReliefWorkerIntegration';


THREE.Mesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const CM_TO_UNIT = 1;
const SUMMIT_TO_UNIT  = 0.1;
const OVERLAY_OPACITY = 1;

// Smooth deformation constants
const DEFAULT_FALLOFF_MULTIPLIER = 2.5; // Falloff radius as multiple of average edge length
const MIN_FALLOFF_RADIUS = 0.5; // Minimum falloff radius in world units
const MAX_FALLOFF_RADIUS = 5.0; // Maximum falloff radius in world units

// Deformation modes for carve/relief operation
const DEFORMATION_MODE = {
  UNIFORM: 'uniform',     // Old behavior: flat displacement inside curve region
  GRADIENT: 'gradient'    // New behavior: center-to-edge gradient falloff
};
const DEFAULT_DEFORMATION_MODE = DEFORMATION_MODE.GRADIENT; // Default to gradient for better results

// Curve visualization constants
const MIN_POINTS_PER_REGION = 3;
const PERFORMANCE_RESOLUTION = 50;
const DEFAULT_CURVE_RESOLUTION = 50;
const HIGH_ACCURACY_RESOLUTION = 200;
const POINT_SIZE = 0.2;
const HANDLE_SIZE = 0.15;
const CURVE_WIDTH = 4.0;
const POINT_COLORS = {
  default: '#E1C200',
  selected: '#FF6B6B', 
  hover: '#FFD93D'
};
const HANDLE_COLOR_IN = '#53DEA4';   // Red for IN handles
const HANDLE_COLOR_OUT = '#4A90E2';  // Blue for OUT handles
const RENDER_ORDER_POINTS = 1000;
const RENDER_ORDER_CURVE = 1e6;
const RENDER_ORDER_HANDLES = 6e5; // Higher than overlay (5e5) to render on top of painted regions
const RENDER_ORDER_POINT_LABELS = 8e5; // Higher than handles for point numbers
const RENDER_ORDER_ARROWS = 7e5; // Even higher than handles

// Debug flag for curve normal arrows - set to false to disable
// This shows green arrow markers indicating the curve normals (perpendicular to curve tangent)
// at curve points for debugging curve normal calculation
const SHOW_NORMAL_ARROWS = false;

const CarveReliefHandler = () => {
    const [scene, shadedMesh, material, controls, camera, renderer, setInteractiveObjects, setPaintingCursor, ghostModeEnabled] = useEditorStore(
        useShallow((s) => {
            const refs = s.sceneRefs || {};
            return [refs.scene, refs.shadedMesh, refs.material, refs.controls, refs.camera, refs.renderer, s.setInteractiveObjects, s.setPaintingCursor, s.ghostModeEnabled];
        })
    );

    const [scoopRecharge, setScoopRecharge, isScoopRechargeApplied, blockingMask] = useRectificationStore(
        useShallow((s) => [s.scoopRecharge, s.setScoopRecharge, s.isScoopRechargeApplied, s.blockingZone?.mask])
    );

    // Worker pool is accessed directly via geometryWorkerPool import for better control over:
    // 1. Queue flushing on abort
    // 2. Debouncing at the right level (preview update, not individual worker calls)
    // 3. Proper cancellation propagation

    // ---- Refs / caches ------------------------------------------------------
    const abortControllerRef = useRef(null); // For cancelling in-flight preview operations
    const operationIdRef = useRef(0);        // Operation ID to prevent race conditions with loading state
    const pendingOperationRef = useRef(null); // Promise tracking pending async operation for apply synchronization
    const overlayMeshRef = useRef({});      // Colored vertex overlay on shadedMesh for zones
    const curveGroupRef = useRef({});       // Visual curve groups per zone
    const pointGroupRef = useRef({});       // Point groups per zone
    const handleGroupRef = useRef({});      // Handle groups per zone
    const arrowGroupRef = useRef({});       // Normal arrow groups per zone (debug)
    const labelGroupRef = useRef({});       // Point number labels per zone

    // Mouse interaction refs
    const isDragging = useRef(false);
    const draggingPoint = useRef({ zoneId: null, pointIndex: null });
    const draggingHandle = useRef({ zoneId: null, pointIndex: null, handleType: null });
    const raycaster = useRef(new THREE.Raycaster());
    const mouse = useRef(new THREE.Vector2());
    const bvhRef = useRef(null);
    const pointNormalsRef = useRef(new Map()); // Cache for point normals per zone
    const handleDragPlaneRef = useRef(new THREE.Plane());
    const handleAnchorRef = useRef(new THREE.Vector3());
    const lastGeometryRef = useRef(null); // track geometry changes for overlay invalidation

    // OPTIMIZATION: Geometry and material caching to prevent recreation
    const sharedGeometryCache = useRef({
      pointSphere: null,
      handleSphere: null
    });
    const sharedMaterialCache = useRef({
      pointDefault: null,
      pointSelected: null,
      handleIn: null,
      handleOut: null,
      handleLineIn: null,
      handleLineOut: null
    });

    // OPTIMIZATION: Curve projection cache per zone
    const curveCache = useRef(new Map()); // Map<zoneId, {points, handles, projectedCurve, curveNormals}>

    // OPTIMIZATION: Dirty tracking for incremental updates
    const dirtyZones = useRef(new Set()); // Track which zones need visual updates

    // OPTIMIZATION: Object pooling for point and handle spheres (reuse instead of recreate)
    const spherePoolRef = useRef({
      point: { available: [], inUse: new Map() },  // Map<key, mesh>
      handleIn: { available: [], inUse: new Map() },
      handleOut: { available: [], inUse: new Map() }
    });

    // OPTIMIZATION: Cache handle line objects to prevent recreation on every frame
    const handleLinesRef = useRef(new Map()); // Map<zoneId, Map<pointIndex, {inLine, outLine}>

    // OPTIMIZATION: RAF throttling for drag updates
    const rafRef = useRef(null);
    const pendingStateUpdate = useRef(null);

    // OPTIMIZATION: Store curves cache keyed by zone ID for useMemo
    const zoneCurvesCacheRef = useRef(new Map()); // Map<zoneId, {points, handles, curve}>

    // OPTIMIZATION: Debounce curve regeneration during drag
    // Prevents expensive curve recalculation on every frame during point/handle drag
    const curveUpdateTimeoutRef = useRef(null);
    const pendingCurveUpdatesRef = useRef(new Set()); // Zones pending curve update after debounce
    const debouncedCurveUpdateMs = 150; // Wait 150ms after drag ends before updating curves

    // OPTIMIZATION: Debounce preview mesh updates to prevent worker overload
    // When user makes rapid changes (dragging handles), we debounce the expensive worker computation
    const previewUpdateTimeoutRef = useRef(null);
    const previewUpdateDebounceMs = 250; // Wait 250ms after last change before updating preview
    const isPreviewUpdatePending = useRef(false); // Track if a preview update is queued

    // OPTIMIZATION: Cache zone deformation data to avoid recomputing unchanged zones
    // This dramatically speeds up preview when only 1-2 zones are modified out of many
    // Structure: Map<zoneId, { pointsHash, handlesHash, summitValue, easing, vertices, weights, deformationMode }>
    const zoneDeformationCacheRef = useRef(new Map());

    // quick accessors
    const selectedZoneId = scoopRecharge?.selectedZoneId ?? 0;

    // ---- OPTIMIZATION: Helper functions for caching and disposal ------------

    /**
     * Create a hash key for zone deformation cache invalidation
     * Returns a string that uniquely identifies the zone's curve configuration
     */
    function createZoneCacheKey(zone, easing) {
      // Create a compact representation of points and handles for comparison
      // Using JSON.stringify is simple but effective for our use case
      const pointsHash = JSON.stringify(zone.points || []);
      const handlesHash = JSON.stringify(zone.bezierHandles || []);
      const summitValue = parseFloat(zone.summitValue) || 0;
      
      return `${pointsHash}|${handlesHash}|${summitValue}|${easing}`;
    }

    /**
     * Check if a zone's deformation data is cached and still valid
     * Returns the cached data if valid, null otherwise
     */
    function getCachedZoneDeformation(zone, easing) {
      const cached = zoneDeformationCacheRef.current.get(zone.id);
      if (!cached) return null;
      
      // Create current hash and compare
      const currentHash = createZoneCacheKey(zone, easing);
      if (cached.hash === currentHash) {
        return cached;
      }
      
      return null; // Cache miss - zone has changed
    }

    /**
     * Store zone deformation data in cache
     */
    function setCachedZoneDeformation(zone, easing, vertices, weights, deformationMode) {
      const hash = createZoneCacheKey(zone, easing);
      
      zoneDeformationCacheRef.current.set(zone.id, {
        hash,
        vertices,
        weights,
        deformationMode
      });
    }

    /**
     * Invalidate cache for a specific zone (called when zone is modified)
     */
    function invalidateZoneDeformationCache(zoneId) {
      zoneDeformationCacheRef.current.delete(zoneId);
    }

    /**
     * Clear all zone deformation caches
     */
    function clearAllZoneDeformationCaches() {
      zoneDeformationCacheRef.current.clear();
    }

    /**
     * Properly dispose of all children in a group (fixes memory leaks)
     */
    function disposeGroup(group) {
      if (!group) return;

      group.children.forEach(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
        // Dispose texture for sprites (labels)
        if (child.material?.map) {
          child.material.map.dispose();
        }
      });
      group.clear();
    }

    /**
     * Initialize shared geometries (created once, reused everywhere)
     */
    function ensureSharedGeometries() {
      if (!sharedGeometryCache.current.pointSphere) {
        sharedGeometryCache.current.pointSphere = new THREE.SphereGeometry(POINT_SIZE, 16, 12);
      }
      if (!sharedGeometryCache.current.handleSphere) {
        sharedGeometryCache.current.handleSphere = new THREE.SphereGeometry(HANDLE_SIZE, 8, 6);
      }
    }

    /**
     * Initialize shared materials (created once, reused everywhere)
     */
    function ensureSharedMaterials() {
      if (!sharedMaterialCache.current.pointDefault) {
        sharedMaterialCache.current.pointDefault = new THREE.MeshBasicMaterial({
          color: POINT_COLORS.default,
          transparent: false,
          opacity: 0.8
        });
      }
      if (!sharedMaterialCache.current.pointSelected) {
        sharedMaterialCache.current.pointSelected = new THREE.MeshBasicMaterial({
          color: POINT_COLORS.selected,
          transparent: false,
          opacity: 0.8
        });
      }
      if (!sharedMaterialCache.current.handleIn) {
        sharedMaterialCache.current.handleIn = new THREE.MeshBasicMaterial({
          color: HANDLE_COLOR_IN,
          transparent: true,
          opacity: 1,
          depthTest: false
        });
      }
      if (!sharedMaterialCache.current.handleOut) {
        sharedMaterialCache.current.handleOut = new THREE.MeshBasicMaterial({
          color: HANDLE_COLOR_OUT,
          transparent: true,
          opacity: 1,
          depthTest: false
        });
      }
      if (!sharedMaterialCache.current.handleLineIn) {
        sharedMaterialCache.current.handleLineIn = new THREE.LineBasicMaterial({
          color: HANDLE_COLOR_IN,
          opacity: 0.6,
          transparent: true
        });
      }
      if (!sharedMaterialCache.current.handleLineOut) {
        sharedMaterialCache.current.handleLineOut = new THREE.LineBasicMaterial({
          color: HANDLE_COLOR_OUT,
          opacity: 0.6,
          transparent: true
        });
      }
    }

    /**
     * Invalidate curve cache for a specific zone
     * Now uses debouncing during drag to avoid expensive recalculations
     */
    function invalidateCurveCache(zoneId) {
      // OPTIMIZATION: Don't immediately invalidate during drag
      // Instead, mark for update after debounce period
      if (isDragging.current) {
        // Mark this zone as needing a curve update
        pendingCurveUpdatesRef.current.add(zoneId);

        // Clear existing timeout
        if (curveUpdateTimeoutRef.current) {
          clearTimeout(curveUpdateTimeoutRef.current);
        }

        // Set new timeout to invalidate curves after drag ends
        curveUpdateTimeoutRef.current = setTimeout(() => {
          // Invalidate all pending zones
          pendingCurveUpdatesRef.current.forEach(zId => {
            zoneCurvesCacheRef.current.delete(zId);
            curveCache.current.delete(zId);
            dirtyZones.current.add(zId);
          });
          pendingCurveUpdatesRef.current.clear();

          // Trigger visual update for these zones
          updateZoneVisuals();
        }, debouncedCurveUpdateMs);
      } else {
        // Not dragging, invalidate immediately
        zoneCurvesCacheRef.current.delete(zoneId);
        curveCache.current.delete(zoneId);
        dirtyZones.current.add(zoneId);
      }
    }

    /**
     * Dispose shared resources on cleanup
     */
    function disposeSharedResources() {
      // Dispose shared geometries
      Object.values(sharedGeometryCache.current).forEach(geom => {
        if (geom) geom.dispose();
      });
      sharedGeometryCache.current = { pointSphere: null, handleSphere: null };

      // Dispose shared materials
      Object.values(sharedMaterialCache.current).forEach(mat => {
        if (mat) mat.dispose();
      });
      sharedMaterialCache.current = {
        pointDefault: null,
        pointSelected: null,
        handleIn: null,
        handleOut: null,
        handleLineIn: null,
        handleLineOut: null
      };

      // Clear curve cache
      curveCache.current.clear();
      dirtyZones.current.clear();

      // Dispose all pooled spheres
      ['point', 'handleIn', 'handleOut'].forEach(type => {
        spherePoolRef.current[type].available.forEach(mesh => {
          if (mesh.geometry) mesh.geometry.dispose();
          if (mesh.material) mesh.material.dispose();
        });
        spherePoolRef.current[type].available = [];
        spherePoolRef.current[type].inUse.clear();
      });

      // Clear debounce timers and pending updates
      if (curveUpdateTimeoutRef.current) {
        clearTimeout(curveUpdateTimeoutRef.current);
      }
      if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
      }
      pendingCurveUpdatesRef.current.clear();
      isPreviewUpdatePending.current = false;

      // Clear curve cache
      zoneCurvesCacheRef.current.clear();

      // Clear zone deformation cache
      zoneDeformationCacheRef.current.clear();
    }

    /**
     * Get a sphere from the pool or create a new one
     */
    function getSphereFromPool(sphereType, position, zoneId, pointIndex, handleType = null) {
      const pool = spherePoolRef.current[sphereType];
      let sphere;

      // Try to reuse from available pool
      if (pool.available.length > 0) {
        sphere = pool.available.pop();
        // Update position
        sphere.position.set(position.x, position.y, position.z);
        // CRITICAL: Update userData when reusing from pool
        // Otherwise the sphere will have old zoneId/pointIndex causing wrong handle selection
        sphere.userData = {
          zoneId: zoneId,
          pointIndex: pointIndex,
          isControlPoint: sphereType === 'point',
          isHandle: sphereType === 'handleIn' || sphereType === 'handleOut',
          handleType: handleType
        };
      } else {
        // Create new sphere if pool is empty
        if (sphereType === 'point') {
          sphere = createPointSphere(position, zoneId, pointIndex, false);
        } else if (sphereType === 'handleIn') {
          sphere = createHandleSphere(position, zoneId, pointIndex, 'in');
        } else if (sphereType === 'handleOut') {
          sphere = createHandleSphere(position, zoneId, pointIndex, 'out');
        }
      }

      // Track in-use sphere
      const key = `${zoneId}_${pointIndex}${handleType ? '_' + handleType : ''}`;
      pool.inUse.set(key, sphere);
      return sphere;
    }

    /**
     * Return a sphere to the pool for reuse
     */
    function returnSphereToPool(sphereType, zoneId, pointIndex, handleType = null) {
      const pool = spherePoolRef.current[sphereType];
      const key = `${zoneId}_${pointIndex}${handleType ? '_' + handleType : ''}`;

      const sphere = pool.inUse.get(key);
      if (sphere) {
        pool.inUse.delete(key);
        pool.available.push(sphere);
        // Remove from scene if it has a parent
        if (sphere.parent) {
          sphere.parent.remove(sphere);
        }
      }
    }

    /**
     * Clear all pooled spheres and return them for reuse
     */
    function returnAllSpheresToPool(zoneId) {
      ['point', 'handleIn', 'handleOut'].forEach(sphereType => {
        const pool = spherePoolRef.current[sphereType];
        const keysToReturn = [];

        // Find all spheres for this zone
        pool.inUse.forEach((sphere, key) => {
          if (key.startsWith(`${zoneId}_`)) {
            keysToReturn.push(key);
          }
        });

        // Return them to available pool
        keysToReturn.forEach(key => {
          const sphere = pool.inUse.get(key);
          if (sphere && sphere.parent) {
            sphere.parent.remove(sphere);
          }
          pool.inUse.delete(key);
          pool.available.push(sphere);
        });
      });

      // Also clean up handle lines for this zone
      const zoneLines = handleLinesRef.current.get(zoneId);
      if (zoneLines) {
        zoneLines.forEach((lineObjects, pointIndex) => {
          if (lineObjects.inLine && lineObjects.inLine.parent) {
            lineObjects.inLine.parent.remove(lineObjects.inLine);
            if (lineObjects.inLine.geometry) lineObjects.inLine.geometry.dispose();
          }
          if (lineObjects.outLine && lineObjects.outLine.parent) {
            lineObjects.outLine.parent.remove(lineObjects.outLine);
            if (lineObjects.outLine.geometry) lineObjects.outLine.geometry.dispose();
          }
        });
        handleLinesRef.current.delete(zoneId);
      }
    }

    // Initialize BVH for raycasting
    useEffect(() => {
        if (shadedMesh && !shadedMesh.geometry.boundsTree) {
            shadedMesh.geometry.computeBoundsTree();
        }
        bvhRef.current = shadedMesh?.geometry.boundsTree;
    }, [shadedMesh]);

  // Ensure each zone has required curve data structure
  useEffect(() => {
    const zones = scoopRecharge?.zones || [];
    const needsInit = zones.some(z => !Array.isArray(z.points) || !Array.isArray(z.bezierHandles));
    if (needsInit) {
      const patched = zones.map(z => ({
        ...z,
        points: Array.isArray(z.points) ? z.points : [],
        bezierHandles: Array.isArray(z.bezierHandles) ? z.bezierHandles : [],
        // Legacy support - keep influences for now during migration
        influences: z.influences || null
      }));
      
      // Check if the patched zones are actually different from current zones
      const hasChanges = patched.some((patchedZone, index) => {
        const currentZone = zones[index];
        if (!currentZone) return true;
        
        // Check if points array changed
        if (JSON.stringify(patchedZone.points) !== JSON.stringify(currentZone.points)) return true;
        
        // Check if bezierHandles array changed
        if (JSON.stringify(patchedZone.bezierHandles) !== JSON.stringify(currentZone.bezierHandles)) return true;
        
        return false;
      });
      
      // Only update if there are actual changes
      if (hasChanges) {
        setScoopRecharge({ zones: patched });
      }
    }
  }, [scoopRecharge?.zones, setScoopRecharge]);

  // Adjust preview mesh material based on ghost mode
  useEffect(() => {
    const previewMesh = scoopRecharge?.previewMesh;

    // Only update if preview mesh exists
    if (!previewMesh || !previewMesh.material) return;

    if (ghostModeEnabled) {
      // Ghost mode ON: Make preview completely opaque
      previewMesh.material.opacity = 1.0;
      previewMesh.material.transparent = false;
    } else {
      // Ghost mode OFF: Make preview transparent
      previewMesh.material.opacity = 0.6;
      previewMesh.material.transparent = true;
    }

    previewMesh.material.needsUpdate = true;
  }, [ghostModeEnabled, scoopRecharge?.previewMesh]);

  // ---- helpers ------------------------------------------------------------
  function ensureGroups(zoneId) {
    if (!scene) return;
    
    // Ensure point group exists for this zone
    if (!pointGroupRef.current[zoneId]) {
      const group = new THREE.Group();
      group.name = `CarveReliefPoints_${zoneId}`;
      scene.add(group);
      pointGroupRef.current[zoneId] = group;
    }
    
    // Ensure curve group exists for this zone
    if (!curveGroupRef.current[zoneId]) {
      const group = new THREE.Group();
      group.name = `CarveReliefCurve_${zoneId}`;
      scene.add(group);
      curveGroupRef.current[zoneId] = group;
    }
    
    // Ensure handle group exists for this zone
    if (!handleGroupRef.current[zoneId]) {
      const group = new THREE.Group();
      group.name = `CarveReliefHandles_${zoneId}`;
      scene.add(group);
      handleGroupRef.current[zoneId] = group;
    }
    
    // Ensure arrow group exists for this zone (debug)
    if (SHOW_NORMAL_ARROWS && !arrowGroupRef.current[zoneId]) {
      const group = new THREE.Group();
      group.name = `CarveReliefArrows_${zoneId}`;
      scene.add(group);
      arrowGroupRef.current[zoneId] = group;
    }
    
    // Ensure label group exists for this zone
    if (!labelGroupRef.current[zoneId]) {
      const group = new THREE.Group();
      group.name = `CarveReliefLabels_${zoneId}`;
      scene.add(group);
      labelGroupRef.current[zoneId] = group;
    }
  }
  
  function ensureOverlay(){
    if (!overlayMeshRef.current && shadedMesh){
      const zones = scoopRecharge?.zones || [];
      for (const z of zones){
        ensureZoneOverlay(z.id);
      }
    }
  }

  function ensureZoneOverlay(zoneId) {
    // Check if geometry has changed (e.g., after alignment transformation)
    const geometryChanged = lastGeometryRef.current &&
      shadedMesh &&
      shadedMesh.geometry.attributes.position !== lastGeometryRef.current;

    // Invalidate all cached overlays if geometry changed
    if (geometryChanged) {
      Object.values(overlayMeshRef.current).forEach(overlay => {
        if (overlay && overlay.parent) {
          overlay.parent.remove(overlay);
        }
        overlay?.geometry?.dispose();
        overlay?.material?.dispose();
      });
      overlayMeshRef.current = {};
    }

    if (overlayMeshRef.current[zoneId]) {
      return overlayMeshRef.current[zoneId];
    }
    if (!shadedMesh) return null;

    const base = shadedMesh.geometry;
    lastGeometryRef.current = base.attributes.position; // track current geometry
    const overlayGeom = new THREE.BufferGeometry();
    if (base.index) overlayGeom.setIndex(base.index);
    overlayGeom.setAttribute('position', base.attributes.position);
    overlayGeom.setAttribute('normal', base.attributes.normal);

    const count = base.attributes.position.count;
    // We no longer need a color attribute, only the paint influence
    const paint = new Float32Array(count);
    overlayGeom.setAttribute('paint', new THREE.BufferAttribute(paint, 1));

    // **OPTIMIZATION**: The material now takes a `uColor` uniform
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(1, 1, 1) },
      },
      vertexShader: `
        precision highp float;
        attribute float paint;
        varying float vPaint;
        void main() {
          vPaint = paint;
          vec3 pos = position + normal * 0.0006;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform vec3 uColor; // Use the color uniform
        varying float vPaint;
        void main() {
          float alpha = smoothstep(0.48, 0.52, vPaint);
          if (alpha < 0.01) discard;
          // Apply gamma correction to the uniform color
          vec3 col = pow(clamp(uColor, 0.0, 1.0), vec3(0.5));
          gl_FragColor = vec4(col, alpha);
          #include <colorspace_fragment>
          #include <dithering_fragment>
        }
      `,
      transparent: true,
      blending: THREE.NormalBlending,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -0.1,
    });

    const overlay = new THREE.Mesh(overlayGeom, mat);
    overlay.name = `ScoopRechargeOverlay_${zoneId}`;
    overlay.renderOrder = 5e5 + zoneId; 
    shadedMesh.add(overlay);
    overlayMeshRef.current[zoneId] = overlay;
    return overlay;
  }

  // ---- PREVIEW MESH IN STORE ---------------------------------------------
  function ensurePreview(){
    const latest = useRectificationStore.getState().scoopRecharge;
    let mesh = latest.previewMesh;
    if (mesh && mesh.isMesh){
      // Check if the preview mesh is still valid for the current shadedMesh
      // If the shadedMesh has changed (e.g., after loading from draft), we need to recreate the preview
      const currentMeshId = shadedMesh?.uuid;
      const previewBasedOnMeshId = mesh.userData?.basedOnMeshId;

      if (currentMeshId && previewBasedOnMeshId !== currentMeshId) {
        // The shadedMesh has changed, dispose the old preview and create a new one
        console.log('ShadedMesh changed, disposing old preview and creating new one');
        disposePreviewFromStore();
        mesh = null; // Reset so we create a new one below
      } else {
        // Make sure it's in the scene
        if (!mesh.parent) scene.add(mesh);
        return mesh;
      }
    }

    if (!shadedMesh) return null;

    // Create new preview from shadedMesh geometry snapshot
    const base = shadedMesh.geometry.clone();
    base.computeVertexNormals();

    const work = base.clone(); // working geometry for preview

    // Attach base+working arrays to the mesh.userData for later reuse
    // Start with transparent preview (will be updated by ghost mode useEffect if needed)
    const mat = CreateCarveReliefPreviewMaterial(0.6);
    mesh = new THREE.Mesh(work, mat);
    mesh.name = 'ScoopRechargePreview';
    mesh.castShadow = shadedMesh.castShadow; mesh.receiveShadow = shadedMesh.receiveShadow;
    mesh.matrixAutoUpdate = true;
    mesh.position.copy(shadedMesh.position); mesh.quaternion.copy(shadedMesh.quaternion); mesh.scale.copy(shadedMesh.scale);
    mesh.visible = false; // Start hidden, will be shown when preview is enabled

    // Persist working data on the mesh itself
    mesh.userData.baseGeometry = base; // cloned base
    mesh.userData.basePos = base.attributes.position.array.slice(0);
    mesh.userData.baseNor = (base.attributes.normal ? base.attributes.normal.array.slice(0) : null);
    if (!mesh.userData.baseNor){ base.computeVertexNormals(); mesh.userData.baseNor = base.attributes.normal.array.slice(0); }
    mesh.userData.accumShift = new Float32Array(work.attributes.position.count);
    mesh.userData.basedOnMeshId = shadedMesh.uuid; // Track which mesh this preview is based on

    scene.add(mesh);
    setScoopRecharge({ previewMesh: mesh });
    return mesh;
  }

  function disposePreviewFromStore(){
    const latest = useRectificationStore.getState().scoopRecharge;
    const mesh = latest.previewMesh;
    if (mesh && mesh.isMesh){
      mesh.parent?.remove(mesh);
      mesh.geometry?.dispose?.();
      mesh.material?.dispose?.();
      // Also dispose base snapshot
      mesh.userData?.baseGeometry?.dispose?.();
    }
    setScoopRecharge({ previewMesh: null });
  }

  // ---- Point and Handle Creation/Management -----------------------------------
  function createPointSphere(position, zoneId, pointIndex, isSelected = false) {
    // OPTIMIZATION: Reuse shared geometry and material instead of creating new ones
    ensureSharedGeometries();
    ensureSharedMaterials();

    const geometry = sharedGeometryCache.current.pointSphere;
    const material = isSelected ?
      sharedMaterialCache.current.pointSelected :
      sharedMaterialCache.current.pointDefault;

    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.set(position.x, position.y, position.z);
    sphere.userData = { zoneId, pointIndex, isControlPoint: true };
    sphere.name = `CarveReliefPoint_${zoneId}_${pointIndex}`;
    sphere.renderOrder = RENDER_ORDER_POINTS;
    return sphere;
  }

  function createHandleSphere(position, zoneId, pointIndex, handleType) {
    // OPTIMIZATION: Reuse shared geometry and material instead of creating new ones
    ensureSharedGeometries();
    ensureSharedMaterials();

    const geometry = sharedGeometryCache.current.handleSphere;
    const material = handleType === 'in' ?
      sharedMaterialCache.current.handleIn :
      sharedMaterialCache.current.handleOut;

    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(position);
    sphere.userData = { zoneId, pointIndex, handleType, isHandle: true };
    sphere.name = `CarveReliefHandle_${zoneId}_${pointIndex}_${handleType}`;
    sphere.renderOrder = RENDER_ORDER_HANDLES;
    return sphere;
  }
  
  /**
   * Create a text label showing the point number
   */
  function createPointLabel(position, pointNumber, zoneId, pointIndex) {
    // Create canvas for text rendering
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 64;
    canvas.height = 64;
    
    // Set font and style
    context.font = 'Bold 32px Arial';
    context.fillStyle = '#FFFFFF';
    context.strokeStyle = '#000000';
    context.lineWidth = 3;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    
    // Draw text with outline for better visibility
    const text = (pointNumber + 1).toString(); // Display 1-based numbering
    context.strokeText(text, 32, 32);
    context.fillText(text, 32, 32);
    
    // Create texture and material
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 0.9,
      depthTest: false
    });
    
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(position);
    sprite.position.y += 0.5; // Offset above the point
    sprite.scale.set(0.8, 0.8, 1); // Size of the label
    sprite.userData = { zoneId, pointIndex, isLabel: true };
    sprite.name = `CarveReliefLabel_${zoneId}_${pointIndex}`;
    sprite.renderOrder = RENDER_ORDER_POINT_LABELS;
    
    return sprite;
  }
  
  // Calculate bezier curve tangent at parameter t
  function calculateBezierTangent(t, p0, p1, p2, p3) {
    const u = 1 - t;
    const tangent = new THREE.Vector3();
    
    // First derivative of cubic bezier curve
    tangent.addScaledVector(p0, -3 * u * u);
    tangent.addScaledVector(p1, 3 * u * u - 6 * u * t);
    tangent.addScaledVector(p2, 6 * u * t - 3 * t * t);
    tangent.addScaledVector(p3, 3 * t * t);
    
    return tangent.normalize();
  }
  
  // Calculate curve tangent at a specific point along the curve
  function calculateCurveTangentAtPoint(t, points, bezierHandles) {
    if (!points || points.length < 2) {
      return new THREE.Vector3(1, 0, 0); // Default tangent
    }
    
    // For closed curves, we need to handle the wrapping
    const numSegments = points.length;
    const segmentIndex = Math.floor(t * numSegments);
    const segmentT = (t * numSegments) - segmentIndex;
    
    // Get the current and next point indices (with wrapping for closed curves)
    const currentIndex = segmentIndex % numSegments;
    const nextIndex = (segmentIndex + 1) % numSegments;
    
    const p0 = new THREE.Vector3(points[currentIndex].x, points[currentIndex].y, points[currentIndex].z);
    const p3 = new THREE.Vector3(points[nextIndex].x, points[nextIndex].y, points[nextIndex].z);
    
    // Get handles for this segment
    const handles = bezierHandles[currentIndex];
    if (!handles) {
      // Fallback: simple linear tangent
      return p3.clone().sub(p0).normalize();
    }
    
    const p1 = new THREE.Vector3(
      p0.x + handles.out.x,
      p0.y + handles.out.y,
      p0.z + handles.out.z
    );
    
    const nextHandles = bezierHandles[nextIndex];
    const p2 = new THREE.Vector3(
      p3.x + (nextHandles ? nextHandles.in.x : 0),
      p3.y + (nextHandles ? nextHandles.in.y : 0),
      p3.z + (nextHandles ? nextHandles.in.z : 0)
    );
    
    return calculateBezierTangent(segmentT, p0, p1, p2, p3);
  }
  
  // Calculate curve normal as cross product of tangent and surface normal
  // This gives the outward-pointing normal to the curve (perpendicular to both tangent and surface)
  function calculateCurveNormal(tangent, surfaceNormal) {
    const curveNormal = new THREE.Vector3().crossVectors(tangent, surfaceNormal);
    return curveNormal.normalize();
  }

  /**
   * Calculate center-to-edge falloff weight for gradient deformation mode
   * Weight decreases from 1.0 at center to 0.0 at curve boundary
   * @param {THREE.Vector3} vertex - Vertex position
   * @param {THREE.Vector3} centerPoint - Center point of curve region
   * @param {number} maxRadius - Maximum distance from center to curve boundary
   * @returns {number} - Weight value between 0.0 and 1.0
   */
  function calculateCenterToEdgeFalloff(vertex, centerPoint, maxRadius) {
    if (maxRadius <= 0) return 0.0; // Guard against invalid radius

    // Calculate distance from vertex to center
    const distanceFromCenter = vertex.distanceTo(centerPoint);

    // Normalize distance to [0, 1] range
    const normalizedDistance = Math.min(1.0, distanceFromCenter / maxRadius);

    // Apply smoothstep function for smooth falloff: 1.0 at center, 0.0 at edge
    const t = 1.0 - normalizedDistance;
    const weight = t * t * (3.0 - 2.0 * t); // Smoothstep function

    return Math.max(0.0, Math.min(1.0, weight)); // Clamp to [0, 1]
  }

  // Create arrow geometry for curve normal visualization (debug)
  function createCurveNormalArrow(position, curveNormal, zoneId, pointIndex) {
    if (!SHOW_NORMAL_ARROWS) return null;
    
    const arrowLength = 2.0; // Length of the arrow
    const arrowHeadLength = 0.3; // Length of arrow head
    const arrowHeadWidth = 0.15; // Width of arrow head
    
    // Create arrow geometry
    const geometry = new THREE.ConeGeometry(arrowHeadWidth, arrowHeadLength, 8);
    const material = new THREE.MeshBasicMaterial({
      color: '#00FF00', // Green color for curve normals (different from surface normals)
      transparent: true,
      opacity: 0.8
    });
    
    const arrow = new THREE.Mesh(geometry, material);
    
    // Position the arrow at the curve point
    arrow.position.copy(position);
    
    // Orient the arrow along the curve normal direction
    const direction = curveNormal.clone().normalize();
    const up = new THREE.Vector3(0, 1, 0);
    
    // Create rotation matrix to align with curve normal
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(up, direction);
    arrow.quaternion.copy(quaternion);
    
    // Move the arrow so the tip is at the position (not the center)
    arrow.position.add(direction.clone().multiplyScalar(arrowHeadLength / 2));
    
    arrow.userData = { zoneId, pointIndex, isCurveNormalArrow: true };
    arrow.name = `CarveReliefCurveNormalArrow_${zoneId}_${pointIndex}`;
    arrow.renderOrder = RENDER_ORDER_ARROWS;
    
    return arrow;
  }
  
  function createZoneCurve(zone, customResolution = null, calculateNormals = false) {
    if (!zone.points || zone.points.length < MIN_POINTS_PER_REGION) return null;

    // Use configurable resolution from store or defaults
    const latest = useRectificationStore.getState().scoopRecharge;
    const storeResolution = latest.curveResolution || DEFAULT_CURVE_RESOLUTION;
    const resolution = customResolution ||
      (isDragging.current ? PERFORMANCE_RESOLUTION : storeResolution);

    // OPTIMIZATION: Check cache first
    const cacheKey = `${zone.id}_${resolution}_${calculateNormals}`;
    const cached = curveCache.current.get(zone.id);

    // Use cache if: points/handles unchanged AND resolution matches AND we don't need fresh normals
    if (cached &&
        cached.resolution === resolution &&
        cached.calculateNormals === calculateNormals &&
        JSON.stringify(cached.points) === JSON.stringify(zone.points) &&
        JSON.stringify(cached.handles) === JSON.stringify(zone.bezierHandles)) {

      // Reuse cached projected positions and normals
      const geometry = new LineGeometry();
      geometry.setPositions(cached.projectedPositions);

      const material = new LineMaterial({
        color: zone.color || ZoneColors[(zone.id - 1) % ZoneColors.length],
        linewidth: CURVE_WIDTH,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        depthTest: true
      });

      const el = renderer?.domElement;
      if (el) material.resolution.set(el.clientWidth, el.clientHeight);

      const curve = new Line2(geometry, material);
      curve.computeLineDistances();
      curve.name = `CarveReliefCurve_${zone.id}`;
      curve.renderOrder = RENDER_ORDER_CURVE;
      curve.frustumCulled = false;

      curve.userData = {
        zoneId: zone.id,
        positions: cached.projectedPositions,
        curveNormals: cached.curveNormals,
        surfaceNormals: cached.surfaceNormals
      };

      return curve;
    }

    // Generate curve if not cached
    const baseCurvePositions = generateCurvePoints(
      zone.points,
      zone.bezierHandles,
      resolution
    );

    let projectedPositions = baseCurvePositions;
    let curveNormals = []; // Store curve normals for arrow creation
    let surfaceNormals = []; // Store surface normals for offset calculation
    
    if (shadedMesh && bvhRef.current) {
      projectedPositions = projectCurvePointsOntoMesh(
        baseCurvePositions, 
        shadedMesh, 
        bvhRef.current
      );
      
      const resolution = projectedPositions.length / 3;
      
      // Always calculate surface normals for proper curve positioning
      for (let i = 0; i < projectedPositions.length; i += 3) {
        const point = new THREE.Vector3(
          projectedPositions[i], 
          projectedPositions[i + 1], 
          projectedPositions[i + 2]
        );
        
        // Get surface normal at this point
        const surfaceNormal = getSurfaceNormalAtPoint(
          { x: point.x, y: point.y, z: point.z },
          shadedMesh,
          bvhRef.current
        );
        surfaceNormals.push(surfaceNormal);
        
        // Only calculate expensive tangent and curve normals when needed
        if (calculateNormals || SHOW_NORMAL_ARROWS) {
          // Calculate curve tangent at this point
          const t = (i / 3) / (resolution - 1); // Parameter t for this point
          const curveTangent = calculateCurveTangentAtPoint(t, zone.points, zone.bezierHandles);
          
          // Calculate curve normal as cross product of tangent and surface normal
          const curveNormal = calculateCurveNormal(curveTangent, surfaceNormal);
          curveNormals.push(curveNormal);
        }
      }
      
      // Apply offset to prevent z-fighting
      const offsetAmount = 0.05;
      for (let i = 0; i < projectedPositions.length; i += 3) {
        const normalIndex = Math.floor(i / 3);
        const surfaceNormal = surfaceNormals[normalIndex];
        projectedPositions[i] += surfaceNormal.x * offsetAmount;
        projectedPositions[i + 1] += surfaceNormal.y * offsetAmount;
        projectedPositions[i + 2] += surfaceNormal.z * offsetAmount;
      }
    }
    
    const geometry = new LineGeometry();
    geometry.setPositions(projectedPositions);
    
    const material = new LineMaterial({
      color: zone.color || ZoneColors[(zone.id - 1) % ZoneColors.length],
      linewidth: CURVE_WIDTH,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      depthTest: true
    });
    
    const el = renderer?.domElement;
    if (el) material.resolution.set(el.clientWidth, el.clientHeight);
    
    const curve = new Line2(geometry, material);
    curve.computeLineDistances();
    curve.name = `CarveReliefCurve_${zone.id}`;
    curve.renderOrder = RENDER_ORDER_CURVE;
    curve.frustumCulled = false;
    
    curve.userData = {
      zoneId: zone.id,
      positions: projectedPositions,
      curveNormals: curveNormals, // Store curve normals for arrow creation
      surfaceNormals: surfaceNormals // Store surface normals for reference
    };

    // OPTIMIZATION: Cache the expensive projection and normal calculations
    curveCache.current.set(zone.id, {
      points: JSON.parse(JSON.stringify(zone.points)),
      handles: JSON.parse(JSON.stringify(zone.bezierHandles)),
      resolution: resolution,
      calculateNormals: calculateNormals,
      projectedPositions: projectedPositions,
      curveNormals: curveNormals,
      surfaceNormals: surfaceNormals
    });

    return curve;
  }

  /**
   * OPTIMIZATION: Memoized curve generation
   * Only regenerates curve if points or handles have changed
   * Avoids expensive raycasting and projection calculations
   */
  function getOrCreateCurve(zone, customResolution = null, calculateNormals = false) {
    if (!zone || !zone.points || zone.points.length < MIN_POINTS_PER_REGION) {
      return null;
    }

    // Create a cache key based on zone ID
    const cacheEntry = zoneCurvesCacheRef.current.get(zone.id);

    // Check if cached curve is still valid (points and handles unchanged)
    if (cacheEntry) {
      const pointsMatch = JSON.stringify(cacheEntry.points) === JSON.stringify(zone.points);
      const handlesMatch = JSON.stringify(cacheEntry.handles) === JSON.stringify(zone.bezierHandles);

      if (pointsMatch && handlesMatch) {
        // Return cached curve - no need to regenerate
        return cacheEntry.curve;
      }
    }

    // Points or handles changed, regenerate curve
    const curve = createZoneCurve(zone, customResolution, calculateNormals);

    // Store in cache for next time
    if (curve) {
      zoneCurvesCacheRef.current.set(zone.id, {
        points: JSON.parse(JSON.stringify(zone.points)),
        handles: JSON.parse(JSON.stringify(zone.bezierHandles)),
        curve: curve
      });
    }

    return curve;
  }

  function addPointToZone(zoneId, worldPosition) {
    const latest = useRectificationStore.getState().scoopRecharge;
    const zones = latest.zones || [];
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return;

    // Project point onto mesh surface
    let projectedPoint = worldPosition;

    const pointIndex = zone.points.length;
    const newPoint = {
      x: projectedPoint.x,
      y: projectedPoint.y,
      z: projectedPoint.z
    };

    // Initialize handles in surface normal plane (with BVH-based normal calculation)
    const normal = getSurfaceNormalAtPoint(newPoint, shadedMesh, bvhRef.current);
    const pointNormalKey = `${zoneId}_${pointIndex}`;
    pointNormalsRef.current.set(pointNormalKey, normal);

    const handleLength = 1.5;
    // Create updated points array including the new point for handle calculation
    const updatedPoints = [...zone.points, newPoint];
    const handles = initializeHandlesInNormalPlane(pointIndex, handleLength, updatedPoints, false);

    const updatedZones = zones.map(z => {
      if (z.id === zoneId) {
        return {
          ...z,
          points: [...z.points, newPoint],
          bezierHandles: [...z.bezierHandles, handles]
        };
      }
      return z;
    });

    // OPTIMIZATION: Invalidate cache for this zone
    invalidateCurveCache(zoneId);

    setScoopRecharge({ zones: updatedZones });
  }

  function removePointFromZone(zoneId, pointIndex) {
    const latest = useRectificationStore.getState().scoopRecharge;
    const zones = latest.zones || [];
    const zone = zones.find(z => z.id === zoneId);
    if (!zone || !zone.points || pointIndex >= zone.points.length || pointIndex < 0) return;

    // Don't allow removal if it would leave less than minimum points required
    if (zone.points.length <= MIN_POINTS_PER_REGION) {
      console.warn(`Cannot remove point - minimum ${MIN_POINTS_PER_REGION} points required per region`);
      return;
    }

    // Remove the point and its corresponding bezier handles
    const newPoints = [...zone.points];
    const newBezierHandles = [...zone.bezierHandles];

    newPoints.splice(pointIndex, 1);
    newBezierHandles.splice(pointIndex, 1);

    // Clean up cached normal for this point and renumber subsequent points
    const pointNormalKey = `${zoneId}_${pointIndex}`;
    pointNormalsRef.current.delete(pointNormalKey);

    // Renumber cached normals for points after the removed one
    for (let i = pointIndex; i < zone.points.length - 1; i++) {
      const oldKey = `${zoneId}_${i + 1}`;
      const newKey = `${zoneId}_${i}`;
      const normal = pointNormalsRef.current.get(oldKey);
      if (normal) {
        pointNormalsRef.current.set(newKey, normal);
        pointNormalsRef.current.delete(oldKey);
      }
    }

    const updatedZones = zones.map(z => {
      if (z.id === zoneId) {
        return {
          ...z,
          points: newPoints,
          bezierHandles: newBezierHandles
        };
      }
      return z;
    });

    // OPTIMIZATION: Invalidate cache for this zone
    invalidateCurveCache(zoneId);

    setScoopRecharge({ zones: updatedZones });
  }
  
  // Initialize handles in the surface normal plane (adapted from TrimlineHandler)
  function initializeHandlesInNormalPlane(pointIndex, handleLength = 1.5, pointsArray = null, alignToWorldXZ = false) {
    // Use provided points array or extract from current zone
    const points = pointsArray;
    if (!points || !points[pointIndex]) {
      console.warn('No points array provided or invalid point index');
      return {
        in: { x: -handleLength, y: 0, z: 0 },
        out: { x: handleLength, y: 0, z: 0 }
      };
    }
    
    const point = points[pointIndex];
    
    // Get the normal from our stored normals if available
    const latest = useRectificationStore.getState().scoopRecharge;
    const currentZoneId = latest?.selectedZoneId ?? 0;
    const pointNormalKey = `${currentZoneId}_${pointIndex}`;
    const normal = pointNormalsRef.current.get(pointNormalKey) || new THREE.Vector3(0, 1, 0);
    
    try {
      // If requested, align handles along the intersection line between the world XZ plane
      // (normal = (0,1,0)) and the point's tangent plane (perpendicular to the surface normal).
      if (alignToWorldXZ) {
        const worldUp = new THREE.Vector3(0, 1, 0);
        const dir = new THREE.Vector3().crossVectors(worldUp, normal).normalize();
        // If the surface normal is parallel to worldUp, the cross is near zero; fallback to X axis
        if (dir.length() < 1e-6) {
          dir.set(1, 0, 0);
        }
        return {
          in: {
            x: -dir.x * handleLength,
            y: -dir.y * handleLength,
            z: -dir.z * handleLength
          },
          out: {
            x: dir.x * handleLength,
            y: dir.y * handleLength,
            z: dir.z * handleLength
          }
        };
      }
      
      // Create two orthogonal vectors in the plane perpendicular to the normal
      const tempVec = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const tangent1 = new THREE.Vector3().crossVectors(normal, tempVec).normalize();
      const tangent2 = new THREE.Vector3().crossVectors(normal, tangent1).normalize();
      
      // For better distribution, use neighboring points for tangent calculation
      const prevIndex = pointIndex === 0 ? points.length - 1 : pointIndex - 1;
      const nextIndex = (pointIndex + 1) % points.length;
      
      const prevPoint = points[prevIndex];
      const nextPoint = points[nextIndex];
      
      if (prevPoint && nextPoint && points.length >= 3) {
        // Calculate approximate tangent direction from neighboring points
        const toPrev = new THREE.Vector3(prevPoint.x - point.x, prevPoint.y - point.y, prevPoint.z - point.z);
        const toNext = new THREE.Vector3(nextPoint.x - point.x, nextPoint.y - point.y, nextPoint.z - point.z);
        
        // Project these directions onto the normal plane
        const toPrevProjected = toPrev.clone().sub(normal.clone().multiplyScalar(toPrev.dot(normal))).normalize();
        const toNextProjected = toNext.clone().sub(normal.clone().multiplyScalar(toNext.dot(normal))).normalize();
        
        // Average the directions for a smooth tangent
        const avgTangent = toPrevProjected.clone().add(toNextProjected).normalize();
        
        if (avgTangent.length() > 0.1) { // Valid tangent
          return {
            in: {
              x: -avgTangent.x * handleLength,
              y: -avgTangent.y * handleLength, 
              z: -avgTangent.z * handleLength
            },
            out: {
              x: avgTangent.x * handleLength,
              y: avgTangent.y * handleLength,
              z: avgTangent.z * handleLength
            }
          };
        }
      }
      
      // Fallback: use the primary tangent direction in the normal plane
      return {
        in: {
          x: -tangent1.x * handleLength,
          y: -tangent1.y * handleLength,
          z: -tangent1.z * handleLength
        },
        out: {
          x: tangent1.x * handleLength,
          y: tangent1.y * handleLength,
          z: tangent1.z * handleLength
        }
      };
      
    } catch (error) {
      console.error('Error initializing handles in normal plane:', error);
      // Default fallback handles
      return {
        in: { x: -handleLength, y: 0, z: 0 },
        out: { x: handleLength, y: 0, z: 0 }
      };
    }
  }
  
  // ---- Mesh Analysis Utilities ------------------------------------------------
  
  /**
   * Calculate the average edge length of a mesh for determining falloff radius
   */
  function calculateAverageEdgeLength(geometry) {
    if (!geometry.index) {
      console.warn('Geometry must be indexed for edge length calculation');
      return 1.0; // Fallback value
    }
    
    const positions = geometry.attributes.position;
    const indices = geometry.index.array;
    const vertex = new THREE.Vector3();
    const vertexB = new THREE.Vector3();
    
    let totalLength = 0;
    let edgeCount = 0;
    const processedEdges = new Set();
    
    // Process each face and its edges
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i];
      const b = indices[i + 1];
      const c = indices[i + 2];
      
      // Check each edge of the triangle
      const edges = [
        [Math.min(a, b), Math.max(a, b)],
        [Math.min(b, c), Math.max(b, c)],
        [Math.min(c, a), Math.max(c, a)]
      ];
      
      for (const [v1, v2] of edges) {
        const edgeKey = `${v1}-${v2}`;
        if (!processedEdges.has(edgeKey)) {
          processedEdges.add(edgeKey);
          
          vertex.fromBufferAttribute(positions, v1);
          vertexB.fromBufferAttribute(positions, v2);
          
          const length = vertex.distanceTo(vertexB);
          totalLength += length;
          edgeCount++;
        }
      }
    }
    
    return edgeCount > 0 ? totalLength / edgeCount : 1.0;
  }
  
  /**
   * Calculate distance from a point to the closest point on a curve
   */
  function distanceToClosestCurvePoint(point, curvePoints) {
    let minDistance = Infinity;
    
    for (const curvePoint of curvePoints) {
      const distance = point.distanceTo(curvePoint);
      if (distance < minDistance) {
        minDistance = distance;
      }
    }
    
    return minDistance;
  }
  
  /**
   * Calculate smooth falloff weight based on distance from curve boundary
   */
  function calculateFalloffWeight(distanceFromBoundary, falloffRadius, falloffType = 'smoothstep') {
    if (distanceFromBoundary <= 0) return 1.0; // Inside the curve
    if (distanceFromBoundary >= falloffRadius) return 0.0; // Outside falloff range
    
    const normalizedDistance = distanceFromBoundary / falloffRadius;
    
    switch (falloffType) {
      case 'linear':
        return 1.0 - normalizedDistance;
        
      case 'cubic':
        const t = 1.0 - normalizedDistance;
        return t * t * t;
        
      case 'smoothstep':
      default:
        const t2 = 1.0 - normalizedDistance;
        return t2 * t2 * (3.0 - 2.0 * t2); // Smoothstep function
    }
  }

  // ---- Curve-to-Mesh Intersection Logic -------------------------------------
  /**
   * Check if a vertex is inside the curve region
   * @param {THREE.Vector3} vertex - The vertex to check
   * @param {Array} curvePoints - Array of curve points (Vector3 or flat array)
   * @param {Array} curveNormals - Array of curve normals (perpendicular to curve)
   * @param {Array} curveSurfaceNormals - Array of surface normals at curve points (for back-face rejection)
   * @param {THREE.Vector3} vertexNormal - Surface normal at the vertex position (for back-face rejection)
   * @returns {boolean} - True if vertex is inside the curve region
   */
  function isVertexInsideCurveRegion(vertex, curvePoints, curveNormals = null, curveSurfaceNormals = null, vertexNormal = null) {
    if (!curvePoints || curvePoints.length < 2) return false;

    // Convert flat positions to Vector3 array if needed
    const points = [];
    if (curvePoints[0]?.x !== undefined) {
      // Already Vector3 objects
      points.push(...curvePoints);
    } else {
      // Flat array - convert to Vector3
      for (let i = 0; i < curvePoints.length; i += 3) {
        points.push(new THREE.Vector3(
          curvePoints[i],
          curvePoints[i + 1],
          curvePoints[i + 2]
        ));
      }
    }

    // Get the closest curve point index for all subsequent checks
    const idx = getClosestIndex3D(vertex, points);
    const pc = points[idx];

    // **CRITICAL FIX: Back-face rejection - check if vertex is on the same side of mesh as the curve**
    // This prevents vertices on the opposite side of tubular meshes from being selected
    if (curveSurfaceNormals && vertexNormal && curveSurfaceNormals[idx]) {
      const curveSurfaceNormal = curveSurfaceNormals[idx];
      
      // Calculate dot product between vertex normal and curve surface normal
      // If they point in opposite directions (dot < 0), vertex is on the back side
      const normalDot = vertexNormal.x * curveSurfaceNormal.x + 
                        vertexNormal.y * curveSurfaceNormal.y + 
                        vertexNormal.z * curveSurfaceNormal.z;
      
      // Threshold for "opposite" normals - if dot < 0.1, normals are pointing away from each other
      // This accounts for some curvature but rejects clear back-facing vertices
      const BACK_FACE_THRESHOLD = 0.1;
      
      if (normalDot < BACK_FACE_THRESHOLD) {
        // Vertex's surface normal points away from curve's surface - it's on the back side
        return false;
      }
    }

    // Use curve normal-based inside/outside determination with center-point validation
    if (Array.isArray(curveNormals) && curveNormals.length === points.length) {
      const nc = curveNormals[idx];
      if (pc && nc) {
        const vx = vertex.x - pc.x;
        const vy = vertex.y - pc.y;
        const vz = vertex.z - pc.z;
        const dot = nc.x * vx + nc.y * vy + nc.z * vz;

        // Determine if curve normals point inward or outward using center point method
        const shouldReverseNormals = shouldReverseCurveNormals(points, curveNormals);

        // If normals should be reversed, flip the dot product sign
        const correctedDot = shouldReverseNormals ? -dot : dot;

        // Positive dot means vertex is on the "inside" side of the curve normal
        return correctedDot > 0;
      }
    }

    // Fallback: use 2D point-in-polygon test on XZ plane
    return isPointInPolygonXZ(vertex, points);
  }
  
  function getClosestIndex3D(point, points) {
    let minDistSq = Infinity;
    let closestIdx = 0;
    
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const distSq = (point.x - p.x) ** 2 + (point.y - p.y) ** 2 + (point.z - p.z) ** 2;
      if (distSq < minDistSq) {
        minDistSq = distSq;
        closestIdx = i;
      }
    }
    
    return closestIdx;
  }
  
  function isPointInPolygonXZ(point, polygonPoints) {
    let inside = false;
    const x = point.x, z = point.z;

    for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
      const xi = polygonPoints[i].x, zi = polygonPoints[i].z;
      const xj = polygonPoints[j].x, zj = polygonPoints[j].z;

      if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / (zj - zi) + xi)) {
        inside = !inside;
      }
    }

    return inside;
  }

  /**
   * Determine if curve normals should be reversed to point inward using center point method
   * @param {Array} points - Array of curve points (Vector3)
   * @param {Array} curveNormals - Array of curve normals (Vector3)
   * @returns {boolean} - True if normals should be reversed to point inward
   */
  function shouldReverseCurveNormals(points, curveNormals) {
    if (!points || !curveNormals || points.length < 3 || curveNormals.length !== points.length) {
      return false; // Can't determine, assume no reversal needed
    }

    // Calculate the center point of all curve control points
    const center = new THREE.Vector3(0, 0, 0);
    for (const point of points) {
      center.add(point);
    }
    center.divideScalar(points.length);

    // For each curve point, check if the curve normal points toward or away from center
    let inwardPointingCount = 0;
    let outwardPointingCount = 0;

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const curveNormal = curveNormals[i];

      // Vector from curve point to center
      const toCenterVector = new THREE.Vector3()
        .subVectors(center, point)
        .normalize();

      // Dot product: positive if normal points toward center (inward), negative if away (outward)
      const dot = curveNormal.dot(toCenterVector);

      if (dot > 0) {
        inwardPointingCount++;
      } else if (dot < 0) {
        outwardPointingCount++;
      }
      // Ignore dots close to zero (perpendicular)
    }

    // If most normals point outward (away from center), we should reverse them
    // to make them point inward for proper inside region selection
    return outwardPointingCount > inwardPointingCount;
  }

  /**
   * Determine if a polygon is wound clockwise or counter-clockwise using the shoelace formula
   * Projects points onto XZ plane for 2D calculation
   */
  function isPolygonClockwise(points) {
    if (points.length < 3) return true; // Default to clockwise for degenerate cases

    let signedArea = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      const xi = points[i].x, zi = points[i].z;
      const xj = points[j].x, zj = points[j].z;
      signedArea += (xj - xi) * (zj + zi);
    }

    // Positive signed area means clockwise in our coordinate system
    return signedArea > 0;
  }

  /**
   * Smooth the distance field to eliminate Voronoi diagram artifacts (sharp creases)
   * Applies Laplacian smoothing to distance values, not vertex positions
   *
   * @param {Array} vertexData - Array of {index, distanceToBoundary} objects
   * @param {THREE.BufferGeometry} geometry - The mesh geometry for adjacency info
   * @param {number} iterations - Number of smoothing iterations (default 3)
   * @param {number} strength - Smoothing strength 0-1 (default 0.5)
   */
  function smoothDistanceField(vertexData, geometry, iterations = 3, strength = 0.5) {
    if (!geometry.index || vertexData.length === 0) return;

    // Build vertex index to array index mapping for quick lookup
    const vertexIndexToDataIndex = new Map();
    for (let i = 0; i < vertexData.length; i++) {
      vertexIndexToDataIndex.set(vertexData[i].index, i);
    }

    // Build adjacency list (only for vertices in our dataset)
    const adjacency = new Map();
    const indices = geometry.index.array;

    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i];
      const b = indices[i + 1];
      const c = indices[i + 2];

      // Only track adjacency for vertices in our inside vertex set
      if (vertexIndexToDataIndex.has(a) || vertexIndexToDataIndex.has(b) || vertexIndexToDataIndex.has(c)) {
        if (!adjacency.has(a)) adjacency.set(a, new Set());
        if (!adjacency.has(b)) adjacency.set(b, new Set());
        if (!adjacency.has(c)) adjacency.set(c, new Set());

        adjacency.get(a).add(b);
        adjacency.get(a).add(c);
        adjacency.get(b).add(a);
        adjacency.get(b).add(c);
        adjacency.get(c).add(a);
        adjacency.get(c).add(b);
      }
    }

    // Apply iterative Laplacian smoothing to distance values
    for (let iter = 0; iter < iterations; iter++) {
      const smoothedDistances = new Float32Array(vertexData.length);

      for (let i = 0; i < vertexData.length; i++) {
        const vData = vertexData[i];
        const vertexIndex = vData.index;
        const neighbors = adjacency.get(vertexIndex);

        if (!neighbors || neighbors.size === 0) {
          smoothedDistances[i] = vData.distanceToBoundary;
          continue;
        }

        // Calculate average distance of neighbors (only those in our dataset)
        let sumDistance = 0;
        let count = 0;

        for (const neighborIndex of neighbors) {
          const dataIndex = vertexIndexToDataIndex.get(neighborIndex);
          if (dataIndex !== undefined) {
            sumDistance += vertexData[dataIndex].distanceToBoundary;
            count++;
          }
        }

        if (count > 0) {
          const avgNeighborDistance = sumDistance / count;
          // Blend original with neighbor average
          smoothedDistances[i] = vData.distanceToBoundary * (1 - strength) + avgNeighborDistance * strength;
        } else {
          smoothedDistances[i] = vData.distanceToBoundary;
        }
      }

      // Update distance values for next iteration
      for (let i = 0; i < vertexData.length; i++) {
        vertexData[i].distanceToBoundary = smoothedDistances[i];
      }
    }
  }

  /**
   * Enhanced vertex classification with distance-to-boundary information for smooth deformation
   * @param {Object} zone - The zone to deform
   * @param {number} falloffRadius - Optional falloff radius
   * @param {string} deformationMode - 'gradient' for center-to-edge falloff, 'uniform' for flat displacement
   * @param {string} easingFunction - Easing function for weight calculation
   * @returns {Object} - {vertices, weights, falloffRadius, deformationMode, easingFunction}
   */
  function getVertexDeformationData(
    zone,
    falloffRadius = null,
    deformationMode = DEFAULT_DEFORMATION_MODE,
    easingFunction = DEFAULT_EASING_FUNCTION
  ) {
    if (!zone.points || zone.points.length < 2) return { vertices: [], weights: [] };
    if (!shadedMesh) return { vertices: [], weights: [] };
    
    // Calculate falloff radius if not provided
    if (falloffRadius === null) {
      const avgEdgeLength = calculateAverageEdgeLength(shadedMesh.geometry);
      falloffRadius = Math.min(MAX_FALLOFF_RADIUS, Math.max(MIN_FALLOFF_RADIUS, avgEdgeLength * DEFAULT_FALLOFF_MULTIPLIER));
    }
    
    // Generate high-resolution curve for precise boundary calculation
    // Moderate resolution balances accuracy with avoiding over-discretization
    const curvePositions = generateCurvePoints(
      zone.points,
      zone.bezierHandles,
      400 // High resolution for accurate distance calculation
    );
    
    let projectedPositions = curvePositions;
    let curveNormals = [];
    let curveSurfaceNormals = []; // **NEW: Store surface normals at curve points for back-face rejection**
    
    if (bvhRef.current) {
      projectedPositions = projectCurvePointsOntoMesh(
        curvePositions,
        shadedMesh,
        bvhRef.current
      );
      
      // Calculate curve normals for each point
      const resolution = projectedPositions.length / 3;
      for (let i = 0; i < projectedPositions.length; i += 3) {
        const point = new THREE.Vector3(
          projectedPositions[i], 
          projectedPositions[i + 1], 
          projectedPositions[i + 2]
        );
        
        // Get surface normal at this point
        const surfaceNormal = getSurfaceNormalAtPoint(
          { x: point.x, y: point.y, z: point.z },
          shadedMesh,
          bvhRef.current
        );
        
        // **Store surface normal for back-face rejection**
        curveSurfaceNormals.push(surfaceNormal.clone());
        
        // Calculate curve tangent at this point
        const t = (i / 3) / (resolution - 1);
        const curveTangent = calculateCurveTangentAtPoint(t, zone.points, zone.bezierHandles);
        
        // Calculate curve normal as cross product of tangent and surface normal
        const curveNormal = calculateCurveNormal(curveTangent, surfaceNormal);
        curveNormals.push(curveNormal);
      }
    }
    
    // Convert to Vector3 array
    const curvePoints = convertToWorldPositions(projectedPositions);

    // Get all mesh vertices and classify them
    const positions = shadedMesh.geometry.attributes.position;
    const normals = shadedMesh.geometry.attributes.normal; // **NEW: Get vertex normals for back-face rejection**
    const vertexCount = positions.count;
    const affectedVertices = [];
    const deformationWeights = [];

    const vertex = new THREE.Vector3();
    const vertexNormal = new THREE.Vector3();
    const normalMatrix = new THREE.Matrix3().getNormalMatrix(shadedMesh.matrixWorld); // For transforming normals

    // ===== GRADIENT MODE: Two-pass distance-to-boundary approach =====
    if (deformationMode === DEFORMATION_MODE.GRADIENT) {
      // Pass 1: Identify inside vertices and calculate distance to boundary
      const insideVertexData = []; // {index, distanceToBoundary}
      let maxDistanceToBoundary = 0;

      for (let i = 0; i < vertexCount; i++) {
        vertex.fromBufferAttribute(positions, i);
        vertex.applyMatrix4(shadedMesh.matrixWorld);

        // **Get vertex normal and transform to world space for back-face rejection**
        vertexNormal.fromBufferAttribute(normals, i);
        vertexNormal.applyMatrix3(normalMatrix).normalize();

        const isInside = isVertexInsideCurveRegion(vertex, curvePoints, curveNormals, curveSurfaceNormals, vertexNormal);

        if (isInside) {
          // Calculate distance from this vertex to nearest curve boundary point
          const distanceToBoundary = distanceToClosestCurvePoint(vertex, curvePoints);

          insideVertexData.push({
            index: i,
            distanceToBoundary: distanceToBoundary
          });

          // Track maximum distance (this is the "center" region)
          if (distanceToBoundary > maxDistanceToBoundary) {
            maxDistanceToBoundary = distanceToBoundary;
          }
        }
      }

      // Guard against edge cases
      if (maxDistanceToBoundary < 1e-6) {
        maxDistanceToBoundary = 1.0; // Prevent division by zero
      }

      // ===== CRITICAL FIX: Aggressive smoothing to remove Voronoi creases =====
      // When control points are close together, Voronoi boundaries create radial creases
      // Strategy: Heavy distance field smoothing + geometric smoothing after displacement
      if (insideVertexData.length > 0) {
        // Aggressive smoothing: eliminate Voronoi creases while preserving overall gradient
        smoothDistanceField(insideVertexData, shadedMesh.geometry, 6, 0.7);

        // Recalculate max after smoothing
        maxDistanceToBoundary = 0;
        for (const vertexData of insideVertexData) {
          if (vertexData.distanceToBoundary > maxDistanceToBoundary) {
            maxDistanceToBoundary = vertexData.distanceToBoundary;
          }
        }
      }

      // Pass 2: Calculate normalized weights based on smoothed distance field
      for (const vertexData of insideVertexData) {
        // Normalize distance: 0 at boundary, 1.0 at furthest interior point
        const normalizedDistance = vertexData.distanceToBoundary / maxDistanceToBoundary;

        // Apply easing function for gradient (configurable artistic control)
        const weight = applyEasing(normalizedDistance, easingFunction);

        if (weight > 1e-6) {
          affectedVertices.push(vertexData.index);
          deformationWeights.push(weight);
        }
      }

    } else {
      // ===== UNIFORM MODE: Original logic =====
      for (let i = 0; i < vertexCount; i++) {
        vertex.fromBufferAttribute(positions, i);
        vertex.applyMatrix4(shadedMesh.matrixWorld);

        // **Get vertex normal and transform to world space for back-face rejection**
        vertexNormal.fromBufferAttribute(normals, i);
        vertexNormal.applyMatrix3(normalMatrix).normalize();

        const isInside = isVertexInsideCurveRegion(vertex, curvePoints, curveNormals, curveSurfaceNormals, vertexNormal);
        let weight = 0.0;

        if (isInside) {
          // Uniform mode: flat displacement inside curve region
          weight = 1.0;
        } else {
          // UNIFORM mode: apply smooth falloff from boundary to falloffRadius
          const distanceToBoundary = distanceToClosestCurvePoint(vertex, curvePoints);
          if (distanceToBoundary <= falloffRadius) {
            // Vertex is within falloff range
            weight = calculateFalloffWeight(distanceToBoundary, falloffRadius, 'smoothstep');
          }
        }

        // Only include vertices that will be affected (weight > 0)
        if (weight > 1e-6) {
          affectedVertices.push(i);
          deformationWeights.push(weight);
        }
      }
    }
    
    return {
      vertices: affectedVertices,
      weights: deformationWeights,
      falloffRadius: falloffRadius,
      deformationMode: deformationMode,
      easingFunction: easingFunction
    };
  }

  /**
   * Apply Laplacian smoothing to specified vertices for boundary refinement
   */
  function applyLaplacianSmoothing(geometry, vertexIndices, iterations = 1, strength = 0.5) {
    if (!geometry.index) {
      console.warn('Geometry must be indexed for Laplacian smoothing');
      return;
    }
    
    const positions = geometry.attributes.position;
    const indices = geometry.index.array;
    const vertexCount = positions.count;
    
    // Build adjacency list for vertices
    const adjacency = new Array(vertexCount).fill(null).map(() => new Set());
    
    for (let i = 0; i < indices.length; i += 3) {
      const a = indices[i];
      const b = indices[i + 1];
      const c = indices[i + 2];
      
      adjacency[a].add(b);
      adjacency[a].add(c);
      adjacency[b].add(a);
      adjacency[b].add(c);
      adjacency[c].add(a);
      adjacency[c].add(b);
    }
    
    // Convert sets to arrays for easier access
    const neighbors = adjacency.map(set => Array.from(set));
    
    // Create vertex set for O(1) lookup
    const smoothVertexSet = new Set(vertexIndices);
    
    // Temporary storage for new positions
    const newPositions = new Float32Array(positions.array.length);
    newPositions.set(positions.array);
    
    const originalPos = new THREE.Vector3();
    const neighborPos = new THREE.Vector3();
    const laplacian = new THREE.Vector3();
    
    for (let iter = 0; iter < iterations; iter++) {
      // Calculate Laplacian for each vertex to smooth
      for (const vertexIndex of vertexIndices) {
        const vertexNeighbors = neighbors[vertexIndex];
        if (vertexNeighbors.length === 0) continue;
        
        // Get original position
        originalPos.fromBufferAttribute(positions, vertexIndex);
        
        // Calculate average of neighboring positions
        laplacian.set(0, 0, 0);
        for (const neighborIndex of vertexNeighbors) {
          neighborPos.fromBufferAttribute(positions, neighborIndex);
          laplacian.add(neighborPos);
        }
        laplacian.divideScalar(vertexNeighbors.length);
        
        // Calculate Laplacian vector (average - original)
        laplacian.sub(originalPos);
        
        // Apply smoothing with strength factor
        const newPos = originalPos.clone().add(laplacian.multiplyScalar(strength));
        
        // Update position
        const baseIndex = vertexIndex * 3;
        newPositions[baseIndex] = newPos.x;
        newPositions[baseIndex + 1] = newPos.y;
        newPositions[baseIndex + 2] = newPos.z;
      }
      
      // Update positions for next iteration
      if (iter < iterations - 1) {
        for (const vertexIndex of vertexIndices) {
          const baseIndex = vertexIndex * 3;
          positions.setXYZ(vertexIndex, 
            newPositions[baseIndex], 
            newPositions[baseIndex + 1], 
            newPositions[baseIndex + 2]
          );
        }
      }
    }
    
    // Apply final positions
    for (const vertexIndex of vertexIndices) {
      const baseIndex = vertexIndex * 3;
      positions.setXYZ(vertexIndex, 
        newPositions[baseIndex], 
        newPositions[baseIndex + 1], 
        newPositions[baseIndex + 2]
      );
    }
    
    positions.needsUpdate = true;
  }
  
  /**
   * Identify boundary vertices that need smoothing (vertices with weights between 0.1 and 0.9)
   */
  function getBoundaryVerticesForSmoothing(vertices, weights, threshold = 0.1) {
    const boundaryVertices = [];
    
    for (let i = 0; i < vertices.length; i++) {
      const weight = weights[i];
      if (weight > threshold && weight < (1.0 - threshold)) {
        boundaryVertices.push(vertices[i]);
      }
    }
    
    return boundaryVertices;
  }
  
  // Legacy painting function for backwards compatibility - will be removed
  function paintAt(hitPointWorld, isDragging = false) {
    if (!shadedMesh) return;
  
    const latest = useRectificationStore.getState().scoopRecharge;
    const currentSelectedZoneId = latest?.selectedZoneId ?? 0;
    const zone = latest.zones.find(z => z.id === currentSelectedZoneId);
    if (!zone) return;
  
    // Use the SAME base geometry that rebuildPreview uses
    const baseGeom =
      latest.previewMesh?.userData?.baseGeometry || shadedMesh.geometry;
    const basePos = baseGeom.attributes.position;
    const vertexCount = basePos.count;
  
    // Ensure influences buffer exists & matches vertex count
    if (!zone.influences || zone.influences.length !== vertexCount) {
      zone.influences = new Float32Array(vertexCount);
    }
  
    const brushRadiusWorld = (brushRadiusRef.current ?? 1) * CM_TO_UNIT;
    
    // Interpolate between last and current position when dragging
    const strokePositions = [];
    
    if (isDragging && lastPaintPosRef.current) {
      // Calculate distance between last and current position
      const lastPos = lastPaintPosRef.current;
      const distance = lastPos.distanceTo(hitPointWorld);
      
      // Determine number of interpolation steps based on distance
      // More steps for longer distances to ensure smooth coverage
      const stepSize = brushRadiusWorld * 0.2; // Step at 20% of brush radius for good overlap
      const numSteps = Math.max(1, Math.ceil(distance / stepSize));
      
      // Interpolate positions
      for (let step = 0; step <= numSteps; step++) {
        const t = step / numSteps;
        const interpPos = new THREE.Vector3().lerpVectors(lastPos, hitPointWorld, t);
        strokePositions.push(interpPos);
      }
    } else {
      // Single click or first drag point
      strokePositions.push(hitPointWorld);
    }
    
    // Store current position for next iteration
    lastPaintPosRef.current = hitPointWorld.clone();
    
    // Apply paint at each interpolated position
    let influencesChanged = false;
    const brushRadiusSq = brushRadiusWorld * brushRadiusWorld;
    
    // Adjust accumulation strength based on number of positions
    // This prevents over-accumulation when many interpolated positions overlap
    const accumulationStrength = isDragging ? 0.5 / Math.sqrt(strokePositions.length) : 0.5;
    
    for (const strokePos of strokePositions) {
      // Convert stroke position to local space
      const strokeLocal = new THREE.Vector3().copy(strokePos);
      shadedMesh.worldToLocal(strokeLocal);
      
      // Paint at this position
      for (let i = 0; i < vertexCount; i++) {
        const bx = basePos.getX(i), by = basePos.getY(i), bz = basePos.getZ(i);
        
        const dx = bx - strokeLocal.x;
        const dy = by - strokeLocal.y;
        const dz = bz - strokeLocal.z;
        
        const d2 = dx*dx + dy*dy + dz*dz;
        if (d2 >= brushRadiusSq) continue; // outside brush, skip
        
        // Smoother falloff function for better edges
        const nd2 = d2 / brushRadiusSq;     // (d/r)^2
        const t   = 1.0 - nd2;              // 1 - (d/r)^2
        
        // Use a smoother cubic falloff instead of quartic for softer edges
        const falloff = t * t * (3.0 - 2.0 * t); // Smoothstep function
        const add = falloff * accumulationStrength;
        
        // Use max operation instead of pure addition for more consistent results
        const old = zone.influences[i];
        const nu  = Math.max(old, old + add * (1.0 - old)); // Blend towards 1.0
        
        if (nu > old) {
          zone.influences[i] = nu >= 1.0 ? 1.0 : nu;
          influencesChanged = true;
        }
      }
    }
  
    if (influencesChanged) {
      // Trigger state change (new zone object is enough for React/Zustand)
      const updatedZones = latest.zones.map(z =>
        z.id === currentSelectedZoneId ? { ...z, influences: zone.influences } : z
      );
      setScoopRecharge({ zones: updatedZones });
      
      // Use lightweight update during interaction, full rebuild only when preview is visible
      if (latest?.previewVisible) {
        rebuildPreview();
      } else {
        updateCurveVisualsOnly();
      }
    }
  }


// ---- Zone Visualization Management -----------------------------------------
function updateZoneVisuals(zoneId = null) {
    const latest = useRectificationStore.getState().scoopRecharge;
    const zones = latest.zones || [];
    const selectedZoneId = latest.selectedZoneId;
    const showControlsForAllZones = latest.showControlsForAllZones;

    // OPTIMIZATION: Initialize shared resources once
    ensureSharedGeometries();
    ensureSharedMaterials();

    const zonesToUpdate = zoneId ? zones.filter(z => z.id === zoneId) : zones;

    for (const zone of zonesToUpdate) {
        ensureGroups(zone.id);

        // OPTIMIZATION: Return old spheres to pool instead of disposing
        returnAllSpheresToPool(zone.id);

        // OPTIMIZATION: Properly dispose existing visuals (fixes memory leaks)
        // Note: Point and handle groups will be cleared when spheres are removed from pool
        disposeGroup(curveGroupRef.current[zone.id]);
        disposeGroup(labelGroupRef.current[zone.id]);
        if (SHOW_NORMAL_ARROWS) {
          disposeGroup(arrowGroupRef.current[zone.id]);
        }

        // Determine if this zone should show controls
        const shouldShowControls = showControlsForAllZones || (zone.id === selectedZoneId);

        // Only show controls if zone has points and should show controls
        if (!zone.points || zone.points.length === 0 || !shouldShowControls) {
          // Clear the groups when not showing controls
          disposeGroup(pointGroupRef.current[zone.id]);
          disposeGroup(handleGroupRef.current[zone.id]);
          continue;
        }

        // OPTIMIZATION: Reuse point spheres from pool instead of creating new ones
        zone.points.forEach((point, index) => {
            const sphere = getSphereFromPool('point', point, zone.id, index);
            pointGroupRef.current[zone.id].add(sphere);

            // Add point number label
            const label = createPointLabel(point, index, zone.id, index);
            labelGroupRef.current[zone.id].add(label);
        });
        
        // Create curve if we have at least 2 points
        if (zone.points.length >= 2) {
            // OPTIMIZATION: Use memoized curve generation - only regenerates if points/handles changed
            const curve = getOrCreateCurve(zone, DEFAULT_CURVE_RESOLUTION, SHOW_NORMAL_ARROWS);
            if (curve) {
                curveGroupRef.current[zone.id].add(curve);
                
                // Create curve normal arrows for debugging (if enabled)
                if (SHOW_NORMAL_ARROWS && curve.userData.curveNormals && curve.userData.curveNormals.length > 0) {
                    const positions = curve.userData.positions;
                    const curveNormals = curve.userData.curveNormals;
                    
                    // Create arrows at every nth point to avoid clutter
                    const arrowStep = Math.max(1, Math.floor(positions.length / 3 / 20)); // Max 20 arrows per curve
                    
                    for (let i = 0; i < positions.length; i += 3 * arrowStep) {
                        const pointIndex = Math.floor(i / 3);
                        if (pointIndex < curveNormals.length) {
                            const position = new THREE.Vector3(
                                positions[i],
                                positions[i + 1],
                                positions[i + 2]
                            );
                            const curveNormal = curveNormals[pointIndex];
                            
                            const arrow = createCurveNormalArrow(position, curveNormal, zone.id, pointIndex);
                            if (arrow) {
                                arrowGroupRef.current[zone.id].add(arrow);
                            }
                        }
                    }
                }
            }
        }
        
        // Show handles based on the visibility setting
        // For selected zone or if showing all zone controls
        if (shouldShowControls) {
            zone.points.forEach((point, index) => {
                const handles = zone.bezierHandles[index];
                if (handles) {
                    const inPos = new THREE.Vector3(
                        point.x + handles.in.x,
                        point.y + handles.in.y,
                        point.z + handles.in.z
                    );
                    const outPos = new THREE.Vector3(
                        point.x + handles.out.x,
                        point.y + handles.out.y,
                        point.z + handles.out.z
                    );

                    // OPTIMIZATION: Reuse handle spheres from pool
                    const inHandle = getSphereFromPool('handleIn', inPos, zone.id, index, 'in');
                    const outHandle = getSphereFromPool('handleOut', outPos, zone.id, index, 'out');

                    handleGroupRef.current[zone.id].add(inHandle);
                    handleGroupRef.current[zone.id].add(outHandle);

                    // OPTIMIZATION: Reuse cached handle lines instead of creating new ones every frame
                    const inLineMaterial = sharedMaterialCache.current.handleLineIn;
                    const outLineMaterial = sharedMaterialCache.current.handleLineOut;

                    // Get or create zone lines map
                    if (!handleLinesRef.current.has(zone.id)) {
                      handleLinesRef.current.set(zone.id, new Map());
                    }
                    const zoneLines = handleLinesRef.current.get(zone.id);

                    // Reuse or create in-handle line
                    let inLine = zoneLines.get(`${index}_in`)?.inLine;
                    if (!inLine) {
                      const inLineGeom = new THREE.BufferGeometry().setFromPoints([
                        new THREE.Vector3(point.x, point.y, point.z),
                        inPos
                      ]);
                      inLine = new THREE.Line(inLineGeom, inLineMaterial);
                      inLine.renderOrder = RENDER_ORDER_HANDLES - 1000;
                      inLine.userData = { isHandleLine: true, zoneId: zone.id, pointIndex: index, handleType: 'in' };
                    } else {
                      // Update existing line geometry positions
                      const positions = inLine.geometry.attributes.position.array;
                      positions[0] = point.x;
                      positions[1] = point.y;
                      positions[2] = point.z;
                      positions[3] = inPos.x;
                      positions[4] = inPos.y;
                      positions[5] = inPos.z;
                      inLine.geometry.attributes.position.needsUpdate = true;
                    }
                    handleGroupRef.current[zone.id].add(inLine);
                    zoneLines.set(`${index}_in`, { inLine, outLine: null });

                    // Reuse or create out-handle line
                    let outLine = zoneLines.get(`${index}_out`)?.outLine;
                    if (!outLine) {
                      const outLineGeom = new THREE.BufferGeometry().setFromPoints([
                        new THREE.Vector3(point.x, point.y, point.z),
                        outPos
                      ]);
                      outLine = new THREE.Line(outLineGeom, outLineMaterial);
                      outLine.renderOrder = RENDER_ORDER_HANDLES - 1000;
                      outLine.userData = { isHandleLine: true, zoneId: zone.id, pointIndex: index, handleType: 'out' };
                    } else {
                      // Update existing line geometry positions
                      const positions = outLine.geometry.attributes.position.array;
                      positions[0] = point.x;
                      positions[1] = point.y;
                      positions[2] = point.z;
                      positions[3] = outPos.x;
                      positions[4] = outPos.y;
                      positions[5] = outPos.z;
                      outLine.geometry.attributes.position.needsUpdate = true;
                    }
                    handleGroupRef.current[zone.id].add(outLine);
                    const existingData = zoneLines.get(`${index}_in`) || {};
                    zoneLines.set(`${index}_out`, { ...existingData, outLine });
                    zoneLines.set(`${index}_in`, { ...existingData, inLine });
                }
            });
        }
    }
}

// Update overlays to show curve-based regions
function updateOverlays() {
    const latest = useRectificationStore.getState().scoopRecharge;

    // OPTIMIZATION: Skip overlay updates if preview is not visible
    if (!latest?.previewVisible) {
        return; // Don't waste time updating invisible overlays
    }

    const zones = latest.zones || [];

    const tmpColor = new THREE.Color();
    for (const zone of zones) {
        const overlayMesh = ensureZoneOverlay(zone.id);
        if (!overlayMesh) continue;

        tmpColor.set(zone.color || ZoneColors[(zone.id - 1) % ZoneColors.length]).convertSRGBToLinear();
        overlayMesh.material.uniforms.uColor.value.copy(tmpColor);

        const paintAttr = overlayMesh.geometry.getAttribute('paint');
        if (!paintAttr) continue;

        // Clear existing paint values - no more region rendering
        paintAttr.array.fill(0);

        paintAttr.needsUpdate = true;
    }
}

// Update preview mesh geometry using smooth curve-based deformation (ASYNC with worker pool)
// This is the INTERNAL implementation - call updatePreviewMeshDebounced for debounced version
async function updatePreviewMeshAsyncInternal() {
    // **CRITICAL FIX: Cancel previous operation AND flush worker queue to prevent backed-up operations**
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
    }
    // Flush any pending operations in the worker queue - this is critical for preventing buildup
    geometryWorkerPool.flushQueue();

    // Create new abort controller and operation ID for this operation
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Increment operation ID for this new operation
    operationIdRef.current += 1;
    const currentOperationId = operationIdRef.current;

    // Check if already cancelled before starting
    if (signal.aborted) return;

    // Set worker loading state at the start
    const { setWorkerLoading } = useEditorStore.getState();
    setWorkerLoading(true, 'Computing deformation');

    // Create promise wrapper to track this operation
    const operationPromise = (async () => {
    try {
        const latest = useRectificationStore.getState().scoopRecharge;
        const previewMesh = latest.previewMesh || ensurePreview();
        if (!previewMesh) return;

        const baseGeom = previewMesh.userData.baseGeometry;
        const basePos = baseGeom.attributes.position;
        const baseNor = baseGeom.attributes.normal;
        const workGeom = previewMesh.geometry;
        const outPos = workGeom.attributes.position;

        const zones = latest.zones || [];
        const vertexCount = basePos.count;

        // Reset to base positions first
        for (let i = 0; i < vertexCount; i++) {
            const bx = basePos.getX(i), by = basePos.getY(i), bz = basePos.getZ(i);
            outPos.setXYZ(i, bx, by, bz);
        }

        // Check if cancelled after reset
        if (signal.aborted) return;

        // Track vertices for post-displacement geometric smoothing
        const uniformBoundaryVertices = new Set();  // UNIFORM mode: boundary only
        const gradientAllVertices = new Set();      // GRADIENT mode: all affected vertices

        // **OPTIMIZATION: Track cache hits/misses for debugging**
        let cacheHits = 0;
        let cacheMisses = 0;

        // Apply smooth curve-based displacement for each zone
        for (const zone of zones) {
            // **CRITICAL: Check if cancelled before EACH zone - enables fast cancellation**
            if (signal.aborted) {
                console.log(`[CarveReliefHandler] Preview update cancelled before zone ${zone.id}`);
                return;
            }

            if (!zone.points || zone.points.length < MIN_POINTS_PER_REGION) continue;

            const delta = (parseFloat(zone.summitValue) || 0) * SUMMIT_TO_UNIT;
            if (Math.abs(delta) < 1e-6) continue; // Skip if no displacement

            // **OPTIMIZATION: Check cache first - avoid expensive worker computation for unchanged zones**
            const cachedData = getCachedZoneDeformation(zone, latest.easing);
            
            let vertices, weights, deformationMode;
            
            if (cachedData) {
                // Cache HIT - use cached deformation data (no worker computation needed!)
                vertices = cachedData.vertices;
                weights = cachedData.weights;
                deformationMode = cachedData.deformationMode;
                cacheHits++;
            } else {
                // Cache MISS - compute deformation using worker pool
                cacheMisses++;
                
                // Get vertices with smooth falloff weights using WORKER POOL with abort signal
                const deformationData = await computeVertexDeformationAsync(
                    shadedMesh,
                    zone,
                    DEFORMATION_MODE.GRADIENT, // default mode
                    latest.easing, // Use easing function from store
                    null, // auto-calculate falloff radius
                    signal // Pass abort signal to worker
                );

                // Check if operation was cancelled or failed
                if (!deformationData || signal.aborted) {
                    console.log(`[CarveReliefHandler] Preview update cancelled after zone ${zone.id} computation`);
                    return;
                }

                vertices = deformationData.vertices;
                weights = deformationData.weights;
                deformationMode = deformationData.deformationMode;
                
                // **Store in cache for future use**
                setCachedZoneDeformation(zone, latest.easing, vertices, weights, deformationMode);
            }

            // **OPTIMIZATION: Check abort between heavy operations**
            if (signal.aborted) return;

            // Apply weighted displacement to affected vertices
            for (let i = 0; i < vertices.length; i++) {
                const vertexIndex = vertices[i];
                let weight = weights[i];

                // **Apply blocking zone mask**: reduce weight based on blocking strength
                // blockingMask: 0 = fully active (allow deformation), 1 = fully blocked (no deformation)
                if (blockingMask && blockingMask[vertexIndex] !== undefined) {
                    const blockingStrength = Math.min(1, Math.max(0, blockingMask[vertexIndex]));
                    weight *= (1 - blockingStrength); // Attenuate weight by blocking strength
                }

                // Skip if weight becomes negligible after blocking mask
                if (weight < 1e-6) continue;

                const nx = baseNor.getX(vertexIndex);
                const ny = baseNor.getY(vertexIndex);
                const nz = baseNor.getZ(vertexIndex);

                const currentX = outPos.getX(vertexIndex);
                const currentY = outPos.getY(vertexIndex);
                const currentZ = outPos.getZ(vertexIndex);

                // Apply weighted displacement along surface normal
                const weightedDelta = delta * weight;
                outPos.setXYZ(
                    vertexIndex,
                    currentX + nx * weightedDelta,
                    currentY + ny * weightedDelta,
                    currentZ + nz * weightedDelta
                );
            }

            // ===== COLLECT VERTICES FOR POST-DISPLACEMENT SMOOTHING =====
            if (deformationMode === DEFORMATION_MODE.UNIFORM) {
                // UNIFORM mode: smooth boundaries only (sharp interior edge)
                const boundaryVertices = getBoundaryVerticesForSmoothing(vertices, weights);
                boundaryVertices.forEach(v => uniformBoundaryVertices.add(v));
            } else {
                // GRADIENT mode: smooth ALL affected vertices (eliminate stepping from discrete mesh)
                vertices.forEach(v => gradientAllVertices.add(v));
            }
        }

        // Log cache performance (only in development or when debugging)
        if (cacheHits > 0 || cacheMisses > 0) {
            console.log(`[CarveReliefHandler] Zone deformation cache: ${cacheHits} hits, ${cacheMisses} misses`);
        }

        // ===== APPLY GEOMETRIC SMOOTHING TO DISPLACED MESH =====
        // Final check before expensive smoothing operations
        if (signal.aborted) return;

        // Note: For now, keep smoothing on main thread since it's less expensive than deformation
        // TODO: Move to worker pool if needed

        // UNIFORM mode: light smoothing at boundaries
        if (uniformBoundaryVertices.size > 0) {
            const smoothVertices = Array.from(uniformBoundaryVertices);
            applyLaplacianSmoothing(workGeom, smoothVertices, 1, 0.3);
        }

        // **Check abort before second smoothing pass**
        if (signal.aborted) return;

        // GRADIENT mode: heavy smoothing of all displaced vertices to eliminate sharp peaks
        // Close proximity control points create sharp deformation peaks that need aggressive smoothing
        if (gradientAllVertices.size > 0) {
            const smoothVertices = Array.from(gradientAllVertices);
            // First pass: aggressive smoothing (0.7) for 4 iterations to blend peaks
            applyLaplacianSmoothing(workGeom, smoothVertices, 4, 0.7);
            
            // **Check abort between smoothing passes**
            if (signal.aborted) return;
            
            // Second pass: moderate smoothing (0.5) for 3 iterations for final refinement
            applyLaplacianSmoothing(workGeom, smoothVertices, 3, 0.5);
        }

        // Final check before committing changes
        if (signal.aborted) return;

        outPos.needsUpdate = true;
        workGeom.computeVertexNormals();
        workGeom.computeBoundingSphere?.();
    } catch (error) {
        if (error.name !== 'AbortError') {
            console.error('[CarveReliefHandler] Error updating preview mesh:', error);
        }
        // Don't log abort errors - they're expected when cancelling
    } finally {
        // Only clear worker loading state if this is still the current operation
        // (prevents cancelled operations from clearing the loading state of newer operations)
        if (currentOperationId === operationIdRef.current) {
            setWorkerLoading(false);
        }
    }
    })();

    // Store this operation promise for apply synchronization
    pendingOperationRef.current = operationPromise;

    // Wait for operation to complete
    try {
        await operationPromise;
    } finally {
        // Clear pending operation reference once complete
        if (pendingOperationRef.current === operationPromise) {
            pendingOperationRef.current = null;
        }
        isPreviewUpdatePending.current = false;
    }
}

/**
 * **CRITICAL FIX: Debounced wrapper for preview mesh updates**
 * This prevents worker overload when user makes rapid changes (dragging handles, etc.)
 * The debounce ensures only ONE computation runs after the user stops making changes
 */
async function updatePreviewMeshAsync() {
    // If currently dragging, skip the update entirely - we'll update when drag ends
    // This prevents expensive worker computation on every mouse move frame
    if (isDragging.current) {
        // Mark that we need an update after dragging completes
        isPreviewUpdatePending.current = true;
        return;
    }

    // Clear any pending debounce timeout
    if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
        previewUpdateTimeoutRef.current = null;
    }

    // **CRITICAL: Cancel any in-flight operation AND flush the queue immediately**
    // This prevents backed-up operations from causing long delays
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
    }
    geometryWorkerPool.flushQueue();

    // Debounce the actual computation
    return new Promise((resolve) => {
        previewUpdateTimeoutRef.current = setTimeout(async () => {
            previewUpdateTimeoutRef.current = null;
            try {
                await updatePreviewMeshAsyncInternal();
                resolve();
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('[CarveReliefHandler] Debounced preview update failed:', error);
                }
                resolve(); // Resolve anyway to prevent hanging promises
            }
        }, previewUpdateDebounceMs);
    });
}

// Synchronous wrapper for compatibility (keeps old behavior where needed)
function updatePreviewMesh() {
    updatePreviewMeshAsync().catch(err => {
        if (err?.name !== 'AbortError') {
            console.error('[CarveReliefHandler] Preview update failed:', err);
        }
    });
}

/**
 * **CRITICAL FIX: Immediate (non-debounced) preview update for final apply**
 * Used when applying the tool - bypasses debouncing to ensure geometry is ready
 */
async function updatePreviewMeshImmediate() {
    // Clear any pending debounce
    if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
        previewUpdateTimeoutRef.current = null;
    }
    
    // Run the update immediately (still with abort handling for safety)
    await updatePreviewMeshAsyncInternal();
}

// Lightweight update for curve visualization (overlays + curve visuals)
function updateCurveVisualsOnly() {
    updateOverlays();
    updateZoneVisuals();
}

// Full rebuild including preview mesh geometry (ASYNC to ensure completion before apply)
async function rebuildPreview(force = false) {
    const latest = useRectificationStore.getState().scoopRecharge;

    // Always update overlays (curve-based visualization)
    updateOverlays();

    // Update preview mesh geometry if:
    // 1. Preview is visible, OR
    // 2. Force flag is true (e.g., when applying without preview enabled)
    if (latest?.previewVisible || force) {
        // **CRITICAL FIX: Use immediate (non-debounced) update when force=true**
        // This ensures geometry is ready before cloning in apply command
        if (force) {
            await updatePreviewMeshImmediate();
        } else {
            // Use debounced version for normal preview updates
            await updatePreviewMeshAsync();
        }
    }

    // Update curve visuals
    updateZoneVisuals();
}

  // Legacy function kept for backwards compatibility
  function initializeZoneInfluences(zones, vertexCount) {
    return zones.map(z => {
      // Ensure curve data exists
      const updated = {
        ...z,
        points: z.points || [],
        bezierHandles: z.bezierHandles || []
      };
      
      // Keep legacy influences for gradual migration if needed
      if (!z.influences || z.influences.length !== vertexCount) {
        updated.influences = new Float32Array(vertexCount);
      }
      
      return updated;
    });
  }
  // ---- mount / unmount ----------------------------------------------------
  useEffect(() => {
    const zones = scoopRecharge?.zones || [];
    const vertexCount = shadedMesh?.geometry.attributes.position.count;
    if (!vertexCount || zones.length === 0) return;

    const needsInit = zones.some(z => 
        !Array.isArray(z.points) || 
        !Array.isArray(z.bezierHandles) || 
        (!z.influences || z.influences.length !== vertexCount) ||
        (z.points.length > 0 && z.points.length < MIN_POINTS_PER_REGION)
    );
    
    // Only update if we actually need to initialize and haven't already done so
    if (needsInit) {
        const patched = initializeZoneInfluences(zones, vertexCount);
        // Check if the patched zones are actually different from current zones
        const hasChanges = patched.some((patchedZone, index) => {
            const currentZone = zones[index];
            if (!currentZone) return true;
            
            // Check if points array changed
            if (JSON.stringify(patchedZone.points) !== JSON.stringify(currentZone.points)) return true;
            
            // Check if bezierHandles array changed
            if (JSON.stringify(patchedZone.bezierHandles) !== JSON.stringify(currentZone.bezierHandles)) return true;
            
            // Check if influences array changed
            if (patchedZone.influences && currentZone.influences) {
                if (patchedZone.influences.length !== currentZone.influences.length) return true;
                // Check if all values are the same
                for (let i = 0; i < patchedZone.influences.length; i++) {
                    if (patchedZone.influences[i] !== currentZone.influences[i]) return true;
                }
            } else if (patchedZone.influences !== currentZone.influences) {
                return true;
            }
            
            return false;
        });
        
        // Only update if there are actual changes
        if (hasChanges) {
            setScoopRecharge({ zones: patched });
        }
    }
  }, [scoopRecharge?.zones, shadedMesh, setScoopRecharge]);


  // Clear overlays and preview when shadedMesh changes (e.g., when loading from draft)
  useEffect(() => {
    if (!shadedMesh) return;

    // Clear existing overlays since they reference the old mesh geometry
    Object.values(overlayMeshRef.current).forEach(overlay => {
      if (overlay && overlay.parent) {
        overlay.parent.remove(overlay);
        overlay.geometry?.dispose?.();
        overlay.material?.dispose?.();
      }
    });
    overlayMeshRef.current = {};

    // Clear cached point normals since they're tied to the old mesh
    pointNormalsRef.current.clear();

    // **CRITICAL: Clear zone deformation cache since vertex indices are tied to the old mesh**
    // Cached vertices/weights would be invalid for the new mesh geometry
    clearAllZoneDeformationCaches();
    console.log('[CarveReliefHandler] Mesh changed - cleared zone deformation cache');

    // Clear preview mesh - ensurePreview will handle checking if it needs recreation
    const latest = useRectificationStore.getState().scoopRecharge;
    const currentPreview = latest.previewMesh;
    if (currentPreview && currentPreview.userData?.basedOnMeshId !== shadedMesh.uuid) {
      console.log('ShadedMesh changed, clearing preview mesh for recreation');
      disposePreviewFromStore();
    }
  }, [shadedMesh?.uuid]); // Use uuid to detect actual mesh changes
  
  useEffect(() => {
    if (!scene || !shadedMesh || !camera || !renderer) return;

    ensureOverlay();
    // OPTIMIZATION: Don't register shadedMesh as interactive for cursor hover detection
    // The mesh has 249K vertices and raycasting it on every mousemove causes massive lag
    // Cursor hover is not critical - raycasting overhead isn't worth it
    setInteractiveObjects([]);
    ensurePreview();
    rebuildPreview();

    // Update visuals for all zones
    updateZoneVisuals();

    const dom = renderer.domElement;
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();

    function intersect(ev){
      const r = dom.getBoundingClientRect();
      ndc.set(((ev.clientX - r.left)/r.width)*2-1, -((ev.clientY - r.top)/r.height)*2+1);
      ray.setFromCamera(ndc, camera);
      
      // Use BVH for accelerated raycasting
      if (bvhRef.current && shadedMesh) {
        try {
          // Ensure the ray is properly constructed
          const rayOrigin = ray.origin.clone();
          const rayDirection = ray.direction.clone();
          
          // Transform ray to mesh local space for BVH
          const meshWorldToLocal = new THREE.Matrix4().copy(shadedMesh.matrixWorld).invert();
          rayOrigin.applyMatrix4(meshWorldToLocal);
          rayDirection.transformDirection(meshWorldToLocal);
          
          const bvhRay = new THREE.Ray(rayOrigin, rayDirection);
          const hit = bvhRef.current.raycastFirst(bvhRay);
          
          if (hit) {
            // Transform hit point back to world space
            const worldPoint = hit.point.clone().applyMatrix4(shadedMesh.matrixWorld);
            return { 
              point: worldPoint, 
              face: hit.face, 
              distance: hit.distance,
              faceIndex: hit.faceIndex,
              object: shadedMesh
            };
          }
          return null;
        } catch (error) {
          console.warn('BVH raycast failed, falling back to standard raycast:', error);
          // Fallback to standard raycast
          const hits = ray.intersectObject(shadedMesh, true);
          return hits[0] || null;
        }
      } else {
        const hits = ray.intersectObject(shadedMesh, true);
        return hits[0] || null;
      }
    }

    const onPointerDown = (ev) => {
      try {
        // Ctrl + Alt + Left Click = Remove point from selected zone
        if (ev.ctrlKey && ev.altKey && ev.button === 0 && !isScoopRechargeApplied) {
          const latest = useRectificationStore.getState().scoopRecharge;
          const selectedZoneId = latest?.selectedZoneId;
          if (!selectedZoneId) return;

          const rect = renderer.domElement?.getBoundingClientRect();
          if (!rect) return;

          mouse.current.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
          mouse.current.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;

          raycaster.current.setFromCamera(mouse.current, camera);

          // Check if we're clicking on a point sphere to remove it
          const zones = latest.zones || [];
          const selectedZone = zones.find(z => z.id === selectedZoneId);
          if (selectedZone) {
            const pointGroup = pointGroupRef.current[selectedZoneId];
            if (pointGroup) {
              const pointObjects = pointGroup.children.filter(child => child.userData?.isControlPoint);
              const intersects = raycaster.current.intersectObjects(pointObjects);

              if (intersects.length > 0) {
                const obj = intersects[0].object;
                const userData = obj.userData || {};
                if (userData.isControlPoint && userData.pointIndex !== undefined) {
                  removePointFromZone(selectedZoneId, userData.pointIndex);
                  ev.preventDefault();
                  ev.stopPropagation();
                  return;
                }
              }
            }
          }
        }

        // Alt + Left Click = Add point to selected zone
        if (ev.altKey && !ev.ctrlKey && ev.button === 0 && !isScoopRechargeApplied) {
          const latest = useRectificationStore.getState().scoopRecharge;
          const selectedZoneId = latest?.selectedZoneId;
          if (!selectedZoneId) return;

          const r = dom.getBoundingClientRect();
          ndc.set(((ev.clientX - r.left)/r.width)*2-1, -((ev.clientY - r.top)/r.height)*2+1);
          ray.setFromCamera(ndc, camera);

          // Use BVH for accelerated raycasting
          let hit = null;
          if (bvhRef.current && shadedMesh) {
            try {
              // Transform ray to mesh local space for BVH
              // Note: ray is a THREE.Raycaster, and ray.ray contains the THREE.Ray with origin/direction
              const rayOrigin = ray.ray.origin.clone();
              const rayDirection = ray.ray.direction.clone();
              const meshWorldToLocal = new THREE.Matrix4().copy(shadedMesh.matrixWorld).invert();
              rayOrigin.applyMatrix4(meshWorldToLocal);
              rayDirection.transformDirection(meshWorldToLocal);

              const bvhRay = new THREE.Ray(rayOrigin, rayDirection);
              const bvhHit = bvhRef.current.raycastFirst(bvhRay);

              if (bvhHit) {
                // Transform hit point back to world space
                const worldPoint = bvhHit.point.clone().applyMatrix4(shadedMesh.matrixWorld);
                hit = {
                  point: worldPoint,
                  face: bvhHit.face,
                  distance: bvhHit.distance,
                  faceIndex: bvhHit.faceIndex,
                  object: shadedMesh
                };
              }
            } catch (error) {
              console.warn('BVH raycast failed in Alt+Click, falling back to standard raycast:', error);
              hit = ray.intersectObject(shadedMesh, true)[0];
            }
          } else {
            hit = ray.intersectObject(shadedMesh, true)[0];
          }

          if (hit) {
            addPointToZone(selectedZoneId, hit.point);
            ev.preventDefault();
            ev.stopPropagation();
            return;
          }
        }
        
        // Regular mouse interaction for point/handle selection
        if (!camera || !scene || !renderer) return;
        
        const rect = renderer.domElement?.getBoundingClientRect();
        if (!rect) return;
        
        mouse.current.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.current.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
        
        raycaster.current.setFromCamera(mouse.current, camera);

        // OPTIMIZATION: For regular clicks (no Alt/Ctrl), only check control points/handles
        // Model raycasting is expensive and only needed for adding/deleting points
        // Don't waste time checking the 249K vertex mesh when we just want to select a point!
        const latest = useRectificationStore.getState().scoopRecharge;
        const zones = latest.zones || [];

        // Only check control points/handles for regular clicks - they're much cheaper to raycast
        // Checking a few dozen control point/handle spheres is instant vs. checking 249K vertices
        const allObjects = [];

        zones.forEach(zone => {
          const pointGroup = pointGroupRef.current[zone.id];
          const handleGroup = handleGroupRef.current[zone.id];

          // Include all point spheres
          if (pointGroup) {
            allObjects.push(...pointGroup.children);
          }

          // Include all handle spheres (but NOT the lines connecting them)
          if (handleGroup) {
            handleGroup.children.forEach(child => {
              if (child.userData?.isHandle && !child.userData?.isHandleLine) {
                allObjects.push(child);
              }
            });
          }
        });

        if (allObjects.length === 0) return;

        const intersects = raycaster.current.intersectObjects(allObjects);
        
        if (intersects.length > 0) {
          const obj = intersects[0].object;
          const userData = obj.userData || {};
          
          if (userData.isControlPoint) {
            // Point selected - start dragging
            draggingPoint.current = {
              zoneId: userData.zoneId,
              pointIndex: userData.pointIndex
            };
            isDragging.current = true;
            if (controls) controls.enabled = false;
          } else if (userData.isHandle) {
            // Handle selected - start handle dragging
            draggingHandle.current = {
              zoneId: userData.zoneId,
              pointIndex: userData.pointIndex,
              handleType: userData.handleType
            };
            
            // Set up handle drag plane
            const zone = zones.find(z => z.id === userData.zoneId);
            if (zone && zone.points[userData.pointIndex]) {
              const anchorPoint = zone.points[userData.pointIndex];
              handleAnchorRef.current.set(anchorPoint.x, anchorPoint.y, anchorPoint.z);
              
              const pointNormalKey = `${userData.zoneId}_${userData.pointIndex}`;
              const surfaceNormal = pointNormalsRef.current.get(pointNormalKey);
              if (surfaceNormal) {
                handleDragPlaneRef.current.setFromNormalAndCoplanarPoint(
                  surfaceNormal, 
                  handleAnchorRef.current
                );
              }
            }
            
            isDragging.current = true;
            if (controls) controls.enabled = false;
          }
        }
        
      } catch (error) {
        console.error('Error in onPointerDown:', error);
        isDragging.current = false;
        if (controls) controls.enabled = true;
      }
    };

    const onPointerMove = (ev) => {
      try {
        if (!isDragging.current || !camera || !renderer) return;

        const rect = renderer.domElement?.getBoundingClientRect();
        if (!rect) return;

        mouse.current.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.current.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.current.setFromCamera(mouse.current, camera);

        const latest = useRectificationStore.getState().scoopRecharge;
        const zones = latest.zones || [];

        // OPTIMIZATION: Use RAF to throttle updates to once per frame
        // This prevents 60+ state updates per second during dragging
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        
        if (draggingHandle.current.zoneId) {
          // Handle dragging logic
          const { zoneId, pointIndex, handleType } = draggingHandle.current;
          const zone = zones.find(z => z.id === zoneId);
          if (!zone || !zone.points[pointIndex]) return;
          
          const intersection = new THREE.Vector3();
          if (raycaster.current.ray.intersectPlane(handleDragPlaneRef.current, intersection)) {
            const point = zone.points[pointIndex];
            const relativePos = new THREE.Vector3(
              intersection.x - point.x,
              intersection.y - point.y,
              intersection.z - point.z
            );
            
            // Independent handle lengths with shared direction
            const pointNormalKey = `${zoneId}_${pointIndex}`;
            const surfaceNormal = pointNormalsRef.current.get(pointNormalKey);
            
            let newHandle = relativePos;
            
            if (surfaceNormal) {
              // Project relative position onto the surface tangent plane (perpendicular to surface normal)
              const normalComponent = relativePos.dot(surfaceNormal);
              newHandle = relativePos.clone().sub(surfaceNormal.clone().multiplyScalar(normalComponent));
            }
            
            // Ensure minimum handle length while preserving direction
            const handleLength = newHandle.length();
            if (handleLength < 0.1) {
              // If too short, extend in current direction or fall back to original direction
              const currentHandle = zone.bezierHandles[pointIndex][handleType];
              const fallbackDir = new THREE.Vector3(currentHandle.x, currentHandle.y, currentHandle.z).normalize();
              newHandle = fallbackDir.multiplyScalar(0.1);
            }
            
            // Get current handles to preserve the non-dragged handle's length
            const currentHandles = zone.bezierHandles[pointIndex];
            const currentInHandle = new THREE.Vector3(currentHandles.in.x, currentHandles.in.y, currentHandles.in.z);
            const currentOutHandle = new THREE.Vector3(currentHandles.out.x, currentHandles.out.y, currentHandles.out.z);
            
            // Calculate the direction (normalized)
            const direction = newHandle.clone().normalize();
            
            let inHandle, outHandle;
            
            if (handleType === 'in') {
              // Dragging IN handle - update IN with new position, keep OUT's length but update direction
              inHandle = {
                x: newHandle.x,
                y: newHandle.y,
                z: newHandle.z
              };
              
              // Keep OUT handle's length but reverse direction
              const outLength = currentOutHandle.length();
              outHandle = {
                x: -direction.x * outLength,
                y: -direction.y * outLength,
                z: -direction.z * outLength
              };
            } else {
              // Dragging OUT handle - update OUT with new position, keep IN's length but update direction
              outHandle = {
                x: newHandle.x,
                y: newHandle.y,
                z: newHandle.z
              };
              
              // Keep IN handle's length but reverse direction
              const inLength = currentInHandle.length();
              inHandle = {
                x: -direction.x * inLength,
                y: -direction.y * inLength,
                z: -direction.z * inLength
              };
            }
            
            // OPTIMIZATION: Store update and apply in RAF
            pendingStateUpdate.current = {
              type: 'handle',
              zoneId,
              pointIndex,
              inHandle,
              outHandle
            };

            rafRef.current = requestAnimationFrame(() => {
              if (!pendingStateUpdate.current) return;

              const { type, zoneId, pointIndex, inHandle, outHandle } = pendingStateUpdate.current;
              const latestZones = useRectificationStore.getState().scoopRecharge.zones || [];

              const updatedZones = latestZones.map(z => {
                if (z.id === zoneId) {
                  const newHandles = [...z.bezierHandles];
                  newHandles[pointIndex] = {
                    in: inHandle,
                    out: outHandle
                  };
                  return { ...z, bezierHandles: newHandles };
                }
                return z;
              });

              invalidateCurveCache(zoneId);
              setScoopRecharge({ zones: updatedZones });
              pendingStateUpdate.current = null;
            });
          }
        } else if (draggingPoint.current.zoneId) {
          // Point dragging logic - project onto mesh surface
          const { zoneId, pointIndex } = draggingPoint.current;
          
          if (shadedMesh && bvhRef.current) {
            try {
              // Transform ray to mesh local space for BVH
              const rayOrigin = raycaster.current.ray.origin.clone();
              const rayDirection = raycaster.current.ray.direction.clone();
              const meshWorldToLocal = new THREE.Matrix4().copy(shadedMesh.matrixWorld).invert();
              rayOrigin.applyMatrix4(meshWorldToLocal);
              rayDirection.transformDirection(meshWorldToLocal);
              
              const bvhRay = new THREE.Ray(rayOrigin, rayDirection);
              const meshIntersects = bvhRef.current.raycastFirst(bvhRay);
              
              if (meshIntersects) {
                // Transform hit point back to world space
                const worldPoint = meshIntersects.point.clone().applyMatrix4(shadedMesh.matrixWorld);
                const newPoint = {
                  x: worldPoint.x,
                  y: worldPoint.y,
                  z: worldPoint.z
                };

                // OPTIMIZATION: Store update and apply in RAF
                pendingStateUpdate.current = {
                  type: 'point',
                  zoneId,
                  pointIndex,
                  newPoint
                };

                rafRef.current = requestAnimationFrame(() => {
                  if (!pendingStateUpdate.current) return;

                  const { type, zoneId, pointIndex, newPoint } = pendingStateUpdate.current;
                  const latestZones = useRectificationStore.getState().scoopRecharge.zones || [];

                  const updatedZones = latestZones.map(z => {
                    if (z.id === zoneId) {
                      const newPoints = [...z.points];
                      newPoints[pointIndex] = newPoint;

                      // Update cached normal for this point
                      const normal = getSurfaceNormalAtPoint(newPoint, shadedMesh, bvhRef.current);
                      const pointNormalKey = `${zoneId}_${pointIndex}`;
                      pointNormalsRef.current.set(pointNormalKey, normal);

                      return { ...z, points: newPoints };
                    }
                    return z;
                  });

                  invalidateCurveCache(zoneId);
                  setScoopRecharge({ zones: updatedZones });
                  pendingStateUpdate.current = null;
                });
              }
            } catch (error) {
              console.warn('BVH raycast failed in point dragging, falling back to standard raycast:', error);
              // Fallback to standard raycast
              const hits = raycaster.current.intersectObject(shadedMesh, true);
              if (hits.length > 0) {
                const worldPoint = hits[0].point;
                const newPoint = {
                  x: worldPoint.x,
                  y: worldPoint.y,
                  z: worldPoint.z
                };

                // OPTIMIZATION: Store update and apply in RAF
                pendingStateUpdate.current = {
                  type: 'point',
                  zoneId,
                  pointIndex,
                  newPoint
                };

                rafRef.current = requestAnimationFrame(() => {
                  if (!pendingStateUpdate.current) return;

                  const { type, zoneId, pointIndex, newPoint } = pendingStateUpdate.current;
                  const latestZones = useRectificationStore.getState().scoopRecharge.zones || [];

                  const updatedZones = latestZones.map(z => {
                    if (z.id === zoneId) {
                      const newPoints = [...z.points];
                      newPoints[pointIndex] = newPoint;

                      // Update cached normal for this point
                      const normal = getSurfaceNormalAtPoint(newPoint, shadedMesh, bvhRef.current);
                      const pointNormalKey = `${zoneId}_${pointIndex}`;
                      pointNormalsRef.current.set(pointNormalKey, normal);

                      return { ...z, points: newPoints };
                    }
                    return z;
                  });

                  invalidateCurveCache(zoneId);
                  setScoopRecharge({ zones: updatedZones });
                  pendingStateUpdate.current = null;
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error in onPointerMove:', error);
      }
    };

    const onPointerUp = () => {
      // OPTIMIZATION: Cancel pending RAF and clear state
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingStateUpdate.current = null;

      const wasDragging = isDragging.current;
      isDragging.current = false;
      draggingPoint.current = { zoneId: null, pointIndex: null };
      draggingHandle.current = { zoneId: null, pointIndex: null, handleType: null };
      if (controls) controls.enabled = true;

      // **CRITICAL FIX: Trigger preview update after drag completes if one was pending**
      // During drag, we skip expensive preview updates. Now that drag is done, update the preview.
      if (wasDragging && isPreviewUpdatePending.current) {
        const latest = useRectificationStore.getState().scoopRecharge;
        if (latest?.previewVisible) {
          // Use a small delay to let any pending curve cache invalidations complete
          setTimeout(() => {
            updatePreviewMesh();
          }, 100);
        }
      }
    };

    const removeZoneOverlay = (zoneId) => {
      const overlay = overlayMeshRef.current[zoneId];
      if (overlay) {
        overlay.parent?.remove(overlay);
        overlay.material?.dispose();
        delete overlayMeshRef.current[zoneId];
      }
    };

    dom.addEventListener('pointerdown', onPointerDown);
    dom.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      dom.removeEventListener('pointerdown', onPointerDown);
      dom.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      // make sure controls are re-enabled
      if (controls) controls.enabled = true;

      if (shadedMesh) shadedMesh.visible = true;

      // **CRITICAL FIX: Cancel any pending/in-flight worker operations on unmount**
      // This prevents the "stuck" state when component unmounts during computation
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      geometryWorkerPool.flushQueue();
      
      // Clear debounce timeouts
      if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
        previewUpdateTimeoutRef.current = null;
      }
      
      // Clear worker loading state in case it was left on
      const { setWorkerLoading } = useEditorStore.getState();
      setWorkerLoading(false);

      // cleanup: remove overlay and preview (store)
      const zones = useRectificationStore.getState().scoopRecharge?.zones || [];
      for (const zone of zones) {
        removeZoneOverlay(zone.id);
        
        // Clean up curve visualization groups
        if (pointGroupRef.current[zone.id]) {
          pointGroupRef.current[zone.id].parent?.remove(pointGroupRef.current[zone.id]);
          delete pointGroupRef.current[zone.id];
        }
        if (curveGroupRef.current[zone.id]) {
          curveGroupRef.current[zone.id].parent?.remove(curveGroupRef.current[zone.id]);
          delete curveGroupRef.current[zone.id];
        }
        if (handleGroupRef.current[zone.id]) {
          handleGroupRef.current[zone.id].parent?.remove(handleGroupRef.current[zone.id]);
          delete handleGroupRef.current[zone.id];
        }
        if (labelGroupRef.current[zone.id]) {
          labelGroupRef.current[zone.id].parent?.remove(labelGroupRef.current[zone.id]);
          delete labelGroupRef.current[zone.id];
        }
        if (SHOW_NORMAL_ARROWS && arrowGroupRef.current[zone.id]) {
          arrowGroupRef.current[zone.id].parent?.remove(arrowGroupRef.current[zone.id]);
          delete arrowGroupRef.current[zone.id];
        }
      }
      disposePreviewFromStore();
      // Clear interactive objects and painting cursor
      setInteractiveObjects([]);
      setPaintingCursor({ enabled: false });

      // OPTIMIZATION: Dispose shared cached resources
      disposeSharedResources();
    };
  }, [scene, shadedMesh, camera, renderer, controls]);

  // OPTIMIZATION: Consolidated effect for zone data changes
  // This replaces 3 duplicate effects that were causing 3-4x work duplication
  useEffect(() => {
    if (!shadedMesh) return;

    // Remove overlays for zones that no longer exist
    const currentZones = scoopRecharge?.zones || [];
    const presentIds = new Set(currentZones.map((z) => z.id));

    // Clean up old overlays
    for (const key in overlayMeshRef.current) {
        const id = Number(key);
        if (!presentIds.has(id)) {
            const overlay = overlayMeshRef.current[id];
            if (overlay) {
                overlay.parent?.remove(overlay);
                overlay.material?.dispose?.();
                delete overlayMeshRef.current[id];
            }
        }
    }

    // Clean up old curve visuals and cached deformation data for deleted zones
    for (const key in pointGroupRef.current) {
        const id = Number(key);
        if (!presentIds.has(id)) {
            pointGroupRef.current[id]?.parent?.remove(pointGroupRef.current[id]);
            delete pointGroupRef.current[id];
            curveGroupRef.current[id]?.parent?.remove(curveGroupRef.current[id]);
            delete curveGroupRef.current[id];
            handleGroupRef.current[id]?.parent?.remove(handleGroupRef.current[id]);
            delete handleGroupRef.current[id];
            labelGroupRef.current[id]?.parent?.remove(labelGroupRef.current[id]);
            delete labelGroupRef.current[id];
            if (SHOW_NORMAL_ARROWS && arrowGroupRef.current[id]) {
              arrowGroupRef.current[id]?.parent?.remove(arrowGroupRef.current[id]);
              delete arrowGroupRef.current[id];
            }
            // **OPTIMIZATION: Clean up cached deformation data for deleted zones**
            invalidateZoneDeformationCache(id);
        }
    }

    ensureOverlay();
    updateZoneVisuals(); // Update curve visuals for all zones

    // **CRITICAL FIX: Skip expensive preview rebuild during active drag**
    // The preview will be updated when drag completes (see onPointerUp handler)
    // This prevents dozens of worker computations being queued during drag
    if (isDragging.current) {
      isPreviewUpdatePending.current = true;
      return; // Skip rebuild - will happen after drag ends
    }

    // Always rebuild for zone changes to keep everything in sync
    // Uses debounced version to prevent rapid-fire updates
    (async () => {
      await rebuildPreview();
    })();
  }, [scoopRecharge?.zones, scoopRecharge?.selectedZoneId, scoopRecharge?.showControlsForAllZones, shadedMesh, blockingMask]);

  // Preview mesh visibility follows previewVisible state (like ElongationHandler)
  useEffect(() => {
    const previewMesh = useRectificationStore.getState().scoopRecharge?.previewMesh;

    if (previewMesh) {
      const isPreviewVisible = scoopRecharge?.previewVisible ?? false;

      // Hide preview when tool is applied (finalized) or when preview is explicitly disabled
      const shouldShowPreview = isPreviewVisible && !isScoopRechargeApplied;

      // Show/hide the preview mesh - keep original mesh visible (transparent preview on top)
      previewMesh.visible = shouldShowPreview;

      // If preview is being enabled, force update the preview mesh since we normally skip it
      if (shouldShowPreview) {
        updatePreviewMesh(); // Force update the mesh when preview becomes visible
        updateOverlays();    // Update overlays
        updateZoneVisuals(); // Update visuals
      }
    }
  }, [scoopRecharge?.previewVisible, isScoopRechargeApplied]);

  // Expose rebuild function to store for external calls (like before apply)
  useEffect(() => {
    setScoopRecharge({ rebuildPreview: rebuildPreview });
  }, [setScoopRecharge]);

  // OPTIMIZATION: Removed 2 duplicate effects that were watching scoopRecharge?.zones
  // All zone visual updates are now handled by the consolidated effect above

  return null;
};

// Helper component to show curve editing instructions
const CurveInstructions = () => {
  return (
    <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white p-3 rounded-lg text-sm max-w-xs">
      <h4 className="font-semibold mb-2">Curve Mode Controls:</h4>
      <ul className="space-y-1 text-xs">
        <li>• <kbd className="bg-gray-600 px-1 rounded">Alt + Click</kbd> - Add point to selected zone</li>
        <li>• <strong>Drag points</strong> - Move curve points on surface</li>
        <li>• <strong>Drag handles</strong> - Adjust handle length only (direction fixed)</li>
        <li>• <strong>Minimum 3 points</strong> required per region</li>
        <li>• Higher accuracy with configurable curve sampling</li>
      </ul>
    </div>
  );
}

export default CarveReliefHandler;
export { CurveInstructions };